import { useState, useEffect, useRef, useCallback } from "react";

function pick(obj, paths = []) {
  for (const p of paths) {
    const val = p.split(".").reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), obj);
    if (val !== undefined) return val;
  }
  return undefined;
}

function num(v, fallback = null) {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
  return fallback;
}

function resolveMetrics(result) {
  return (
    result?.AUDIT?.metrics ||
    result?.head_result?.last_event?.AUDIT?.metrics ||
    result?.audit?.metrics ||
    result?.ledger_result?.AUDIT?.metrics ||
    result?.compile_result?.scores ||
    result?.metrics ||
    null
  );
}

function resolveDER(result) {
  return result?.der ?? result?.ENTRY?.meta?.SDI_DER ?? null;
}

function metricAliases(metrics = {}) {
  if (!metrics) return {};
  const rb  = metrics.rai_breakdown ?? {};
  const W   = metrics.weights ?? { W_ILJO: 0.25, W_EGO: 0.25, W_CORR: 0.30, W_SUP: 0.20 };
  function score(explicit, rbKey, wKey) {
    const e = num(explicit);
    if (e !== null) return e;
    const rb_val = num(rb[rbKey]);
    const w      = num(W[wKey]);
    if (rb_val !== null && w !== null && w > 0) {
      const divided = rb_val / w;
      return divided <= 1.05 ? divided : rb_val;
    }
    return null;
  }
  return {
    RAI:      pick(metrics, ["RAI", "rai"]),
    Jc:       pick(metrics, ["Jc_minted", "Jc", "jc"]),
    correctness: score(metrics.Correctness ?? metrics.correctness, "correctness", "W_CORR"),
    ego_der_gca: score(metrics.Ego_bucket_S,  "ego_der_gca", "W_EGO"),
    iljo:        score(null, "iljo", "W_ILJO"),
    superego:    score(metrics.Superego_score ?? metrics.superego, "superego", "W_SUP"),
    der_schema_S:   pick(metrics, ["DER_schema_S", "der_schema_S"]),
    gca_score:      pick(metrics, ["GCA_score", "gca_score"]),
    signal_support_S: pick(metrics, ["Signal_support_S", "signal_support_S"]),
    subquestion_verdict_readiness_S: pick(metrics, ["Subquestion_verdict_readiness_S"]),
    decision_determinism_S: pick(metrics, ["Decision_determinism_S", "decision_determinism_S"]),
    ego_bucket_S:   pick(metrics, ["Ego_bucket_S", "ego_bucket_S"]),
    sigma_resistance: pick(metrics, ["Sigma_resistance", "sigma_resistance"]),
    id_tokens_est:  pick(metrics, ["ID_tokens_est", "id_tokens_est"]),
    rai_version:    pick(metrics, ["rai_version"]),
    mint_min_rai:   pick(metrics, ["thresholds.mint_min_rai", "mint_min_rai"]),
  };
}

function limitingComponent(m) {
  const c = [["correctness",num(m.correctness)],["ego_der_gca",num(m.ego_der_gca)],["iljo",num(m.iljo)],["superego",num(m.superego)]].filter(([,v])=>v!==null);
  if (!c.length) return null;
  c.sort((a,b)=>a[1]-b[1]);
  return { name: c[0][0], value: c[0][1] };
}

function thresholdDelta(rai, t) {
  const r=num(rai), th=num(t);
  return (r===null||th===null)?null:r-th;
}

const LIFECYCLE_STATES = [
  "QUESTION_RECEIVED","SHELL_PROCESSING","DER_GENERATED","COMPILE_RUNNING",
  "COMPILE_RESULT","NORMALIZING","COMMIT_DECISION","APPENDING","CHAIN_VERIFIED","ANSWER_RENDERED",
];

const LIFECYCLE_META = {
  QUESTION_RECEIVED: { label:"Question received",       layer:"UI",     lc:"blue",   note:"Client sends question to the SDI Logic Node — the enforcement boundary between intent and governed state." },
  SHELL_PROCESSING:  { label:"Kernel — context build",  layer:"VULTR",  lc:"cyan",   note:"SDI Logic Node reads agent identity and ledger chain state, builds the governed context passed to the Reasoning Engine." },
  DER_GENERATED:     { label:"DER generated",           layer:"MODEL",  lc:"purple", note:"Reasoning Engine executes the SDI Reasoning Contract — produces a Decision Evidence Record (DER): structured, bounded, evidence-linked reasoning. Not free-form prose." },
  COMPILE_RUNNING:   { label:"Protocol validation",     layer:"WIX",    lc:"amber",  note:"Public Protocol Validator checks the DER against the open reasoning contract: schema completeness, governance anchors, signal quality, ILJO structure." },
  COMPILE_RESULT:    { label:"Compile result",          layer:"WIX",    lc:"amber",  note:"PASS = DER satisfies all contract requirements. FAIL = rejected here, no commit. Gate is deterministic and model-independent." },
  NORMALIZING:       { label:"Canonicalization",        layer:"VULTR",  lc:"cyan",   note:"Logic Node canonicalizes the DER for deterministic hashing — key ordering and whitespace normalized before SHA-384 computation." },
  COMMIT_DECISION:   { label:"Commit decision",         layer:"VULTR",  lc:"cyan",   note:"Commit Audit scores RAI (Reasoning Alignment Index) across four weighted components. RAI >= 0.86 -> COMMIT. Below threshold -> REJECT. No human override at this gate." },
  APPENDING:         { label:"Appending to ledger",     layer:"LEDGER", lc:"green",  note:"SHA-384 hash-chained append. Entry hash = hash(DER content + parent hash). Append-only — no edits, no deletes." },
  CHAIN_VERIFIED:    { label:"Chain verified",          layer:"LEDGER", lc:"green",  note:"Parent hash re-verified after write. chain_ok confirms integrity from genesis. Chain is publicly queryable." },
  // CHANGE 1 — updated note to reference GOVERNED_ANSWER
  ANSWER_RENDERED:   { label:"Answer rendered",         layer:"UI",     lc:"blue",   note:"Answer sourced from committed ILJO.GOVERNED_ANSWER — plain prose extracted from the committed reasoning artifact. Rendered only after ledger_result.written === true." },
};

const ARCH_LAYERS = [
  { layer:"UI",          lc:"blue",   label:"GlassBox Client",    detail:"demo.sdi-protocol.org" },
  { layer:"VULTR",       lc:"cyan",   label:"SDI Logic Node",     detail:"api.sdi-protocol.org — enforcement plane" },
  { layer:"MODEL",       lc:"purple", label:"Reasoning Engine",   detail:"Anthropic · Gemini · OpenAI" },
  { layer:"WIX",         lc:"amber",  label:"Protocol Validator", detail:"sdi-protocol.org/_functions/compile — open contract" },
  { layer:"COMMIT_GATE", lc:"cyan",   label:"Commit Gate",        detail:"api.sdi-protocol.org — RAI scoring · threshold enforcement · chain continuity" },
  { layer:"LEDGER",      lc:"green",  label:"Governed Ledger",    detail:"api.sdi-protocol.org — SHA-384 · append-only · CAS-ordered" },
];

const SUGGESTED_QUESTIONS = [
  { text: "Should we prioritize AI governance investment before deploying autonomous agents?", tag: "GOVERNANCE" },
  { text: "What criteria should determine when an AI system requires human oversight?", tag: "POLICY" },
  { text: "How do we evaluate trust in AI-assisted decisions across institutional boundaries?", tag: "TRUST" },
  { text: "What should I do?", tag: "WILL STRESS TEST", stress: true },
  { text: "Is the economy going to crash?", tag: "WILL STRESS TEST", stress: true },
];

const DEMO_SESSION = {
  agent_id: "SDI-4EDBE05288CB",
  sovereign_hash: "0x4C7C3E55AC86476A025460F8.815",
  contract_version: "SDI_DER_v1.1",
  contract_source: "sdi-protocol.org/_functions/manifest",
  policy_mode: "DEMO",
  model_provider: "anthropic/claude-sonnet-4-6",
  tip_seq: 8,
  chain_ok: true,
};

const LEDGER_HEAD_URL = "https://api.sdi-protocol.org/ledger/head/SDI-4EDBE05288CB";
const LEDGER_LIST_URL = "https://api.sdi-protocol.org/ledger/list/SDI-4EDBE05288CB";
const LEDGER_GET_URL  = (seq) => `https://api.sdi-protocol.org/ledger/get/SDI-4EDBE05288CB/${seq}`;

const GENESIS_ENTRIES = [
  {
    seq: 1, type: "GENESIS", model_provider: "system",
    entry_hash: "0xAA2AF6C440F964ADF61C3432C6F74ADE10901A359FA08AFC8BEC905C851004BC2EE1470F109E80EADE69DF2C06073916",
    parent_hash: null, utc: "2026-03-03T18:41:54Z", rai: 0.9, jc: null, verdict: "PASS",
    intent: "Agent SDI-4EDBE05288CB minted via SDI challenge-verify-mint protocol.",
    note: "Sovereign identity established. Contract: SDI_DER_v1.1. Sovereign hash registered.", der: null,
  },
  {
    seq: 2, type: "POST_MINT_CONTINUITY", model_provider: "system",
    entry_hash: "0xD5B947271CA86D7E7B4AFA325258083C704894D94D4C749303AE11EF172B521F74F38BA7EFB9BB837E537F69802C42DD",
    parent_hash: "0xAA2AF6C440F964ADF61C3432C6F74ADE10901A359FA08AFC8BEC905C851004BC2EE1470F109E80EADE69DF2C06073916",
    utc: "2026-03-03T18:41:55Z", rai: null, jc: null, verdict: "PASS",
    intent: "Post-mint continuity check. Parent hash verified.",
    note: "Chain integrity confirmed. CAS ordering established.", der: null,
  },
  {
    seq: 3, type: "EPISODIC", model_provider: "anthropic/claude-sonnet-4-6",
    entry_hash: "0x50C68661C6C064EF81A531840AD54A4B9E1995AC3CEB78D1A392BE269130A12C01BA898120FAC51778C357B5AC4E84A5",
    parent_hash: "0xD5B947271CA86D7E7B4AFA325258083C704894D94D4C749303AE11EF172B521F74F38BA7EFB9BB837E537F69802C42DD",
    utc: "2026-03-07T00:10:34Z", rai: 0.9758, jc: 93.9467, verdict: "PASS",
    intent: "Authorize autonomous financial AI only when human SOVEREIGNTY is preserved and potential harm (PRIMUM) is bounded by control architecture.",
    question: "What governance controls should be required before an AI agent is authorized to take autonomous financial actions?",
    outcome: "STATE=AUTHORIZED_CONDITIONAL. Authorization granted only when all seven controls are implemented, documented, tested, and operated by trained personnel with clear escalation paths. Human authority over consequential action is preserved. Harm potential is bounded by hard limits and real-time oversight.",
    note: "First live governed turn via Vultr orchestrator. RAI 0.9758. Jc 93.9467. All 4 anchors present.",
    der: {
      DECISION_INTENT: {
        intent_statement: "Identify necessary governance controls for authorizing AI agents to execute autonomous financial transactions.",
        context_framing: "Autonomous financial systems require governance frameworks to preserve human authority and mitigate harm from algorithmic failure, market manipulation, or unintended consequences.",
        role: "analyst", tier: "Strategic", impact_domain: "FINANCIAL",
      },
      QUESTION_LOGIC: {
        strategic_question: "What governance controls are necessary and sufficient to authorize autonomous financial AI without compromising SOVEREIGNTY or PRIMUM?",
        sub_questions: [
          {
            id: "q1",
            prompt: "What control architectures do regulatory bodies and financial institutions currently require for autonomous trading and fund management?",
            linked_signal_ids: ["sig_001"],
            success_standard: {
              definition: "Evidence of published governance frameworks from SEC, CFTC, or major financial institutions demonstrating control requirements.",
              framework_source: { name: "SEC Rule 10b-5 / Market Abuse Regulation (EU) + NIST AI RMF GOVERN 3.1", url: "https://www.sec.gov/", retrieved_utc: "2026-03-07T00:10:16Z" },
            },
          },
          {
            id: "q2",
            prompt: "What human oversight and circuit-breaker mechanisms are essential to preserve human authority over consequential financial actions?",
            linked_signal_ids: ["sig_002"],
            success_standard: {
              definition: "Documented best practices on kill-switches, approval thresholds, audit trails, and escalation protocols from financial AI governance literature.",
              framework_source: { name: "NIST AI RMF GOVERN 2.3 / Algorithmic Accountability Framework", url: "https://airc.nist.gov/AI_RMF_1.0/govern", retrieved_utc: "2026-03-07T00:10:16Z" },
            },
          },
        ],
      },
      SYSTEM_INPUT: {
        inputs: [
          {
            signal_id: "sig_001", source_system: "WEB",
            raw_value: "SEC and CFTC mandate pre-trade risk controls, position limits, real-time surveillance, kill-switch authority, and trader authorization for algorithmic trading systems per Regulation SHO and Market Abuse Regulation.",
            captured_utc: "2026-03-07T00:10:16Z",
            provenance: "Regulatory guidance documented in SEC rule documents and institutional risk management policies.",
            citation: { title: "SEC Regulation SHO / CFTC Algorithmic Trading Requirements", url: "https://www.sec.gov/cgi-bin/browse-edgar", retrieved_utc: "2026-03-07T00:10:16Z" },
            insight_dimensions: { actionability: 5, relevance: 5, predictive_value: 4, specificity: 5, measurability: 5 },
            insight_strength: 4,
          },
          {
            signal_id: "sig_002", source_system: "DOC",
            raw_value: "Essential controls: (1) human sign-off on transaction thresholds; (2) audit trail of all decisions; (3) real-time monitoring dashboards; (4) kill-switch authority vested in human operator; (5) regular backtesting and model validation; (6) segregation of duties.",
            captured_utc: "2026-03-07T00:10:16Z",
            provenance: "Financial institution internal governance policies and NIST AI Risk Management Framework.",
            citation: { title: "NIST AI RMF Govern & Map Functions", url: "https://airc.nist.gov/AI_RMF_1.0/govern", retrieved_utc: "2026-03-07T00:10:16Z" },
            insight_dimensions: { actionability: 5, relevance: 5, predictive_value: 4, specificity: 4, measurability: 4 },
            insight_strength: 4,
          },
        ],
      },
      ILJO: {
        INTENT: "Authorize autonomous financial AI only when human SOVEREIGNTY is preserved and potential harm (PRIMUM) is bounded by control architecture.",
        LOGIC: "Regulatory bodies (SEC, CFTC) and institutional frameworks mandate pre-trade limits, real-time surveillance, and kill-switch authority to prevent market abuse and catastrophic loss. NIST AI RMF and financial governance literature establish that human approval thresholds, audit trails, segregation of duties, and continuous monitoring are baseline controls.",
        JUDGMENT: "VERDICT: AI agents may be authorized for autonomous financial actions only when ALL of the following controls are in place: (1) Pre-trade position and notional exposure limits enforced by system, (2) Real-time surveillance and anomaly detection with human alert, (3) Kill-switch authority vested exclusively in human operator, (4) Human sign-off required for transactions exceeding defined thresholds, (5) Complete audit trail immutable and timestamped, (6) Daily backtesting and weekly model validation, (7) Segregation of duties between model development and deployment authority.",
        OUTCOME: "STATE=AUTHORIZED_CONDITIONAL",
      },
      GCA: {
        SUPEREGO: {
          anchors_required: ["SOVEREIGNTY","PRIMUM","BOUNDEDNESS","STOP_ON_UNCERTAINTY"],
          anchors_present:  ["SOVEREIGNTY","PRIMUM","BOUNDEDNESS","STOP_ON_UNCERTAINTY"],
        },
      },
    },
    metrics: { RAI: 0.9758, Jc_minted: 93.9467, Correctness: 1.0, Ego_bucket_S: 0.9033, Superego_score: 1.0, rai_version: "RAI_v2", rai_breakdown: { iljo: 0.25, ego_der_gca: 0.225833, correctness: 0.3, superego: 0.2 }, weights: { W_ILJO: 0.25, W_EGO: 0.25, W_CORR: 0.30, W_SUP: 0.20 }, thresholds: { mint_min_rai: 0.86, mint_min_jc: 0 } },
    _loaded: true,
  },
  {
    seq: 4, type: "EPISODIC", model_provider: "anthropic/claude-sonnet-4-6",
    entry_hash: "0xF80919261DA42103F46BFF5593BD220D7E61F5478F7DE95D",
    parent_hash: "0x50C68661C6C064EF81A531840AD54A4B9E1995AC3CEB78D1A392BE269130A12C01BA898120FAC51778C357B5AC4E84A5",
    utc: "2026-03-07T17:37:13Z", rai: 0.9758, jc: 99.1659, verdict: "PASS",
    intent: "Determine the minimum required governance controls that must be satisfied before any AI agent is authorized to execute autonomous financial actions.",
    question: "What governance controls should be required before an AI agent is authorized to take autonomous financial actions?",
    outcome: "STATE=CONDITIONALLY_AUTHORIZED",
    der: null, _loaded: false,
    metrics: { RAI: 0.9758, Jc_minted: 99.1659, Correctness: 1.0, Ego_bucket_S: 0.9033, GCA_score: 1.0, Superego_score: 1.0, DER_schema_S: 0.9, rai_version: "RAI_v2", rai_breakdown: { iljo: 0.25, ego_der_gca: 0.225833, correctness: 0.3, superego: 0.2 }, weights: { W_ILJO: 0.25, W_EGO: 0.25, W_CORR: 0.30, W_SUP: 0.20 }, thresholds: { mint_min_rai: 0.86, mint_min_jc: 0 } },
  },
  { seq: 6, type: "system", model_provider: "system", entry_hash: "0x33155D5E995D6DF3244E7C5200BF4B5270513E642A16D8AD", parent_hash: "0x5489E77D0D5772E77D7DEBE6E1403A573C9933FC6C055780", utc: "2026-03-07T21:42:21Z", rai: null, jc: null, verdict: null, intent: "Protocol test — bare curl call, no session context.", der: null, metrics: null, _loaded: true },
  { seq: 7, type: "system", model_provider: "system", entry_hash: "0xA90DFC0D95AFCCEB3E0ACE3BC007905C46892377741D4625", parent_hash: "0x33155D5E995D6DF3244E7C5200BF4B5270513E642A16D8AD", utc: "2026-03-07T21:42:58Z", rai: null, jc: null, verdict: null, intent: "Protocol test — bare curl call, no session context.", der: null, metrics: null, _loaded: true },
  { seq: 8, type: "system", model_provider: "system", entry_hash: "0x991E7206115BC3716524E4A6A7CEC2FC90735A30C4B73674", parent_hash: "0xA90DFC0D95AFCCEB3E0ACE3BC007905C46892377741D4625", utc: "2026-03-07T21:43:28Z", rai: null, jc: null, verdict: null, intent: "Protocol test — bare curl call, no session context.", der: null, metrics: null, _loaded: true },
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600&family=Syne:wght@400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  :root {
    --bg:#080b0f; --bg-panel:#0d1117; --bg-card:#111820; --bg-hover:#161e28;
    --border:#1e2d3d; --border-hi:#2a4060;
    --text:#c9d1d9; --text-dim:#6e7f8f; --text-muted:#3d4f60;
    --green:#3fb950; --green-dim:#1a3d22;
    --red:#f85149;   --red-dim:#3d1a1a;
    --amber:#d29922; --amber-dim:#3d2d0a;
    --blue:#58a6ff;  --blue-dim:#0d2040;
    --purple:#bc8cff; --cyan:#39c5cf;
    --mono:'JetBrains Mono',monospace; --sans:'Syne',sans-serif;
  }
  .gb-root { font-family:var(--mono); background:var(--bg); color:var(--text); height:100vh; display:flex; flex-direction:column; font-size:12px; line-height:1.5; overflow:hidden; }
  .gb-header { background:var(--bg-panel); border-bottom:1px solid var(--border); padding:10px 16px; display:flex; align-items:center; gap:20px; flex-wrap:wrap; position:sticky; top:0; z-index:100; flex-shrink:0; }
  .gb-logo-wrap { display:flex; align-items:center; gap:10px; flex-shrink:0; }
  .gb-logo-img { width:42px; height:42px; object-fit:contain; border-radius:4px; }
  .gb-logo-text { font-family:var(--sans); font-size:14px; font-weight:700; color:var(--cyan); letter-spacing:0.05em; white-space:nowrap; }
  .gb-logo-sub { font-size:9px; color:var(--text-muted); letter-spacing:0.08em; text-transform:uppercase; margin-top:2px; font-family:var(--mono); }
  .gb-header-fields { display:flex; align-items:center; gap:16px; flex-wrap:wrap; flex:1; }
  .gb-field { display:flex; align-items:center; gap:6px; }
  .gb-field-label { color:var(--text-muted); font-size:10px; text-transform:uppercase; letter-spacing:0.08em; }
  .gb-field-value { color:var(--text); font-size:11px; }
  .gb-field-value.green{color:var(--green);} .gb-field-value.blue{color:var(--blue);} .gb-field-value.amber{color:var(--amber);} .gb-field-value.cyan{color:var(--cyan);} .gb-field-value.dim{color:var(--text-dim);}
  .gb-dot { width:6px; height:6px; border-radius:50%; background:var(--green); box-shadow:0 0 6px var(--green); animation:pulse 2s ease-in-out infinite; }
  @keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.4} }
  .gb-model-selector { display:flex; flex-direction:column; align-items:center; gap:4px; margin-left:auto; flex-shrink:0; }
  .gb-model-selector-label { font-size:9px; color:#e2e8f0; text-transform:uppercase; letter-spacing:0.14em; font-weight:700; opacity:1; margin-bottom:2px; }
  .gb-model-track { display:flex; background:var(--bg-card); border:1px solid var(--border); border-radius:20px; padding:3px; gap:2px; }
  .gb-model-opt { display:flex; align-items:center; gap:5px; padding:4px 10px; border-radius:16px; cursor:pointer; transition:all 0.2s; border:none; background:none; font-family:var(--mono); }
  .gb-model-opt.active { background:var(--bg-hover); border:1px solid var(--border-hi); }
  .gb-model-opt-name { font-size:10px; color:var(--text-dim); }
  .gb-model-opt.active .gb-model-opt-name { color:var(--text); }
  .gb-model-opt-version { font-size:8px; color:var(--text-muted); }
  .gb-turn-counter { font-size:10px; color:var(--text-muted); border:1px solid var(--border); border-radius:3px; padding:3px 8px; white-space:nowrap; flex-shrink:0; }
  .gb-turn-counter span { color:var(--amber); }
  .gb-notice-overlay { position:fixed; inset:0; background:rgba(8,11,15,0.7); display:flex; align-items:flex-start; justify-content:center; z-index:200; padding-top:60px; }
  .gb-notice { background:var(--bg-panel); border:1px solid var(--amber); border-radius:6px; padding:20px 24px; max-width:460px; width:90%; }
  .gb-notice-title { font-size:12px; color:var(--amber); font-weight:600; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:10px; display:flex; align-items:center; gap:8px; }
  .gb-notice-body { font-size:11px; color:var(--text-dim); line-height:1.7; margin-bottom:14px; }
  .gb-notice-body strong { color:var(--text); }
  .gb-notice-confirm { width:100%; padding:9px; background:var(--amber-dim); border:1px solid var(--amber); border-radius:4px; color:var(--amber); font-family:var(--mono); font-size:11px; cursor:pointer; transition:all 0.15s; }
  .gb-notice-confirm:hover { background:var(--amber); color:var(--bg); }
  .gb-dock-persist { font-size:9px; color:var(--amber); padding:4px 14px; background:rgba(210,153,34,0.06); border-bottom:1px solid var(--amber-dim); display:flex; align-items:center; gap:6px; flex-shrink:0; }
  .gb-body { display:grid; grid-template-rows:1fr 260px; flex:1; min-height:0; overflow:hidden; }
  .gb-main { display:grid; grid-template-columns:340px 1fr; overflow:hidden; min-height:0; }
  .gb-pane { border-right:1px solid var(--border); display:flex; flex-direction:column; overflow:hidden; }
  .gb-pane:last-child { border-right:none; }
  .gb-pane-header { padding:9px 14px; border-bottom:1px solid var(--border); font-size:10px; text-transform:uppercase; letter-spacing:0.1em; color:var(--text-muted); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
  .gb-pane-header-right { display:flex; align-items:center; gap:6px; }
  .gb-pane-scroll { flex:1; overflow-y:auto; padding:14px; }
  .gb-pane-scroll::-webkit-scrollbar{width:4px;} .gb-pane-scroll::-webkit-scrollbar-thumb{background:var(--border);border-radius:2px;}
  .gb-layer { display:inline-flex; align-items:center; font-size:8px; padding:1px 5px; border-radius:2px; font-weight:500; letter-spacing:0.06em; white-space:nowrap; border:1px solid; text-transform:uppercase; }
  .gb-layer.blue   { color:var(--blue);   background:rgba(88,166,255,0.07);   border-color:rgba(88,166,255,0.25); }
  .gb-layer.cyan   { color:var(--cyan);   background:rgba(57,197,207,0.07);   border-color:rgba(57,197,207,0.25); }
  .gb-layer.purple { color:var(--purple); background:rgba(188,140,255,0.07);  border-color:rgba(188,140,255,0.25); }
  .gb-layer.amber  { color:var(--amber);  background:rgba(210,153,34,0.07);   border-color:rgba(210,153,34,0.25); }
  .gb-layer.green  { color:var(--green);  background:rgba(63,185,80,0.07);    border-color:rgba(63,185,80,0.25); }
  .gb-ledger-dock { border-top:2px solid var(--border); background:var(--bg-panel); display:flex; flex-direction:column; overflow:hidden; }
  .gb-dock-header { padding:7px 14px; border-bottom:1px solid var(--border); font-size:10px; text-transform:uppercase; letter-spacing:0.08em; color:var(--text-muted); display:flex; align-items:center; gap:8px; flex-shrink:0; }
  .gb-dock-scroll { flex:1; overflow-y:auto; }
  .gb-dock-scroll::-webkit-scrollbar{width:3px;} .gb-dock-scroll::-webkit-scrollbar-thumb{background:var(--border);}
  .gb-log-row { border-bottom:1px solid var(--border); }
  .gb-log-row-head { display:flex; align-items:center; gap:0; cursor:pointer; transition:background 0.1s; user-select:none; }
  .gb-log-row-head:hover { background:var(--bg-hover); }
  .gb-log-row.open .gb-log-row-head { background:var(--bg-card); border-bottom:1px solid var(--border-hi); }
  .gb-log-seq  { font-size:9px; color:var(--text-muted); padding:7px 10px 7px 14px; width:32px; flex-shrink:0; }
  .gb-log-caret{ font-size:9px; color:var(--text-muted); width:14px; flex-shrink:0; }
  .gb-log-hash-col { font-family:var(--mono); font-size:9px; color:var(--cyan); padding:7px 10px 7px 0; width:130px; flex-shrink:0; overflow:hidden; white-space:nowrap; }
  .gb-log-type-col { font-size:9px; color:var(--text-dim); padding:7px 8px; width:150px; flex-shrink:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .gb-log-intent-col { font-size:9px; color:var(--text); flex:1; padding:7px 8px; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; line-height:1.4; min-width:0; }
  .gb-log-utc-col { font-size:9px; color:var(--text-muted); padding:7px 10px; white-space:nowrap; flex-shrink:0; }
  .gb-log-rai-col { font-size:9px; padding:7px 14px 7px 4px; flex-shrink:0; white-space:nowrap; }
  .gb-log-rai { font-size:9px; padding:1px 5px; border-radius:2px; }
  .gb-log-rai.pass { background:var(--green-dim); color:var(--green); }
  .gb-log-artifact { padding:12px 14px 14px 56px; background:var(--bg-card); border-top:1px solid var(--border); }
  .gb-artifact-section { margin-bottom:12px; }
  .gb-artifact-label { font-size:9px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:4px; display:flex; align-items:center; gap:6px; }
  .gb-artifact-text { font-size:10px; color:var(--text-dim); line-height:1.6; padding:6px 8px; background:var(--bg); border-radius:3px; border-left:2px solid var(--border-hi); }
  .gb-artifact-text.outcome { border-left-color:var(--green); color:var(--text); }
  .gb-artifact-text.intent { border-left-color:var(--cyan); }
  .gb-artifact-hash-full { font-size:9px; color:var(--cyan); word-break:break-all; line-height:1.8; font-family:var(--mono); padding:5px 8px; background:var(--bg); border-radius:3px; border:1px solid var(--border); letter-spacing:0.02em; }
  .gb-anchor-row { display:flex; flex-wrap:wrap; gap:4px; }
  .gb-anchor-pill { font-size:9px; padding:2px 6px; border-radius:2px; background:var(--green-dim); color:var(--green); border:1px solid rgba(63,185,80,0.3); }
  .gb-signal-card { background:var(--bg); border:1px solid var(--border); border-radius:3px; padding:8px 10px; margin-bottom:6px; }
  .gb-signal-header { display:flex; align-items:flex-start; gap:6px; margin-bottom:4px; }
  .gb-signal-id { font-size:9px; color:var(--text-muted); flex-shrink:0; }
  .gb-signal-source { font-size:8px; padding:1px 4px; border-radius:2px; flex-shrink:0; }
  .gb-signal-source.WEB { background:var(--blue-dim); color:var(--blue); }
  .gb-signal-source.DOC { background:var(--amber-dim); color:var(--amber); }
  .gb-signal-value { font-size:10px; color:var(--text-dim); line-height:1.5; margin-bottom:5px; }
  .gb-citation { display:flex; align-items:center; gap:5px; margin-top:4px; }
  .gb-citation-link { font-size:9px; color:var(--blue); text-decoration:none; }
  .gb-citation-link:hover { text-decoration:underline; }
  .gb-citation-utc { font-size:9px; color:var(--text-muted); }
  .gb-lifecycle { margin-bottom:14px; }
  .gb-section-title { font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:10px; display:flex; align-items:center; gap:8px; }
  .gb-state-row { display:flex; align-items:flex-start; gap:8px; padding:5px 0 5px 12px; border-left:1px solid var(--border); margin-left:5px; position:relative; }
  .gb-state-row::before { content:''; position:absolute; left:-4px; top:10px; width:7px; height:7px; border-radius:50%; background:var(--border); border:1px solid var(--border-hi); }
  .gb-state-row.done::before { background:var(--green); border-color:var(--green); box-shadow:0 0 6px var(--green); }
  .gb-state-row.active::before { background:var(--blue); border-color:var(--blue); box-shadow:0 0 6px var(--blue); animation:pulse 1s ease-in-out infinite; }
  .gb-state-row.fail::before { background:var(--red); border-color:var(--red); }
  .gb-state-body { flex:1; min-width:0; }
  .gb-state-top { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
  .gb-state-icon { font-size:10px; width:12px; flex-shrink:0; }
  .gb-state-label { font-size:11px; flex:1; }
  .gb-state-row.done .gb-state-label{color:var(--text);} .gb-state-row.active .gb-state-label{color:var(--blue);} .gb-state-row.fail .gb-state-label{color:var(--red);} .gb-state-row.pending .gb-state-label{color:var(--text-muted);}
  .gb-state-time { font-size:9px; color:var(--text-muted); white-space:nowrap; }
  .gb-state-note { font-size:9px; color:var(--text-muted); margin-top:2px; line-height:1.5; padding-left:18px; }
  .gb-commit-boundary { margin:8px 0 6px; padding:6px 10px; background:rgba(63,185,80,0.04); border:1px dashed var(--green-dim); border-radius:3px; }
  .gb-commit-boundary-title { font-size:9px; color:var(--green); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:2px; }
  .gb-commit-boundary-note { font-size:9px; color:var(--text-muted); line-height:1.4; }
  .gb-der-viewer { margin-bottom:14px; }
  .gb-expandable { background:var(--bg-card); border:1px solid var(--border); border-radius:4px; margin-bottom:6px; overflow:hidden; }
  .gb-expandable-header { padding:7px 10px; display:flex; align-items:center; justify-content:space-between; cursor:pointer; font-size:11px; color:var(--text-dim); transition:background 0.1s; user-select:none; }
  .gb-expandable-header:hover { background:var(--bg-hover); }
  .gb-expandable-header.pass{color:var(--green);} .gb-expandable-header.fail{color:var(--red);} .gb-expandable-header.info{color:var(--blue);}
  .gb-expandable-label { display:flex; align-items:center; gap:7px; flex-wrap:wrap; flex:1; }
  .gb-expandable-badge { font-size:9px; padding:1px 6px; border-radius:2px; }
  .gb-expandable-badge.pass{background:var(--green-dim);color:var(--green);} .gb-expandable-badge.fail{background:var(--red-dim);color:var(--red);} .gb-expandable-badge.pending{background:var(--border);color:var(--text-muted);}
  .gb-expandable-nist { font-size:9px; color:var(--text-muted); }
  .gb-expandable-body { padding:10px; border-top:1px solid var(--border); }
  .gb-json { font-size:10px; color:var(--text-dim); white-space:pre-wrap; word-break:break-all; line-height:1.6; max-height:260px; overflow-y:auto; }
  .gb-error-list { display:flex; flex-direction:column; gap:4px; }
  .gb-error-item { font-size:10px; color:var(--red); padding:4px 8px; background:var(--red-dim); border-radius:3px; }
  .gb-rai { margin-bottom:14px; }
  .gb-rai-explainer { background:var(--bg-card); border:1px solid var(--border); border-radius:4px; padding:8px 10px; margin-bottom:10px; font-size:10px; color:var(--text-dim); line-height:1.6; }
  .gb-rai-explainer strong { color:var(--text); }
  .gb-rai-rule { font-size:9px; color:var(--amber); padding:4px 8px; background:var(--amber-dim); border-radius:3px; margin-top:6px; }
  .gb-rai-gauge { display:flex; align-items:center; gap:10px; margin-bottom:12px; padding:10px; background:var(--bg-card); border:1px solid var(--border); border-radius:4px; }
  .gb-rai-number { font-size:28px; font-weight:600; line-height:1; min-width:60px; }
  .gb-rai-number.pass{color:var(--green);} .gb-rai-number.fail{color:var(--red);} .gb-rai-number.neutral{color:var(--text-dim);}
  .gb-rai-bar-wrap { flex:1; }
  .gb-rai-bar-track { height:6px; background:var(--bg); border-radius:3px; overflow:hidden; position:relative; margin-bottom:4px; }
  .gb-rai-bar-fill { height:100%; border-radius:3px; transition:width 0.6s ease; }
  .gb-rai-bar-fill.pass{background:var(--green);} .gb-rai-bar-fill.fail{background:var(--red);} .gb-rai-bar-fill.neutral{background:var(--border-hi);}
  .gb-rai-bar-threshold { position:absolute; top:-2px; bottom:-2px; width:1px; background:var(--amber); }
  .gb-rai-delta { font-size:10px; }
  .gb-rai-delta.pos{color:var(--green);} .gb-rai-delta.neg{color:var(--red);} .gb-rai-delta.neutral{color:var(--text-muted);}
  .gb-rai-meta { font-size:10px; color:var(--text-muted); margin-top:2px; }
  .gb-component-row { display:flex; align-items:center; gap:8px; padding:5px 0; border-bottom:1px solid var(--border); }
  .gb-component-row:last-child{border-bottom:none;}
  .gb-component-name { font-size:10px; color:var(--text-dim); width:90px; flex-shrink:0; }
  .gb-component-name.limiter{color:var(--amber);}
  .gb-component-bar { flex:1; height:4px; background:var(--bg); border-radius:2px; overflow:hidden; }
  .gb-component-fill { height:100%; border-radius:2px; transition:width 0.4s ease; }
  .gb-component-fill.high{background:var(--green);} .gb-component-fill.med{background:var(--amber);} .gb-component-fill.low{background:var(--red);}
  .gb-component-val { font-size:10px; color:var(--text); width:28px; text-align:right; flex-shrink:0; }
  .gb-component-nist { font-size:9px; color:var(--text-muted); width:80px; text-align:right; flex-shrink:0; }
  .gb-limiter-callout { font-size:10px; color:var(--amber); padding:5px 8px; background:var(--amber-dim); border-radius:3px; margin-top:8px; }
  .gb-rai-version { font-size:9px; color:var(--text-muted); border:1px solid var(--border); padding:1px 5px; border-radius:2px; }
  .gb-mint-story { background:var(--bg-card); border:1px solid var(--border); border-left:2px solid var(--cyan); border-radius:4px; padding:10px 12px; margin-bottom:12px; }
  .gb-mint-story-title { font-size:10px; color:var(--cyan); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:6px; }
  .gb-mint-story-text { font-size:10px; color:var(--text-dim); line-height:1.6; margin-bottom:4px; }
  .gb-mint-story-field { display:flex; gap:8px; margin-top:4px; font-size:10px; }
  .gb-mint-story-key { color:var(--text-muted); flex-shrink:0; width:90px; }
  .gb-mint-story-val { color:var(--text); overflow:hidden; text-overflow:ellipsis; }
  .gb-suggestions { margin-bottom:16px; }
  .gb-suggestions-label { font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:8px; }
  .gb-suggestion { display:flex; align-items:flex-start; gap:8px; padding:8px 10px; background:var(--bg-card); border:1px solid var(--border); border-radius:4px; margin-bottom:6px; cursor:pointer; transition:all 0.15s; text-align:left; width:100%; font-family:var(--mono); }
  .gb-suggestion:hover { border-color:var(--border-hi); background:var(--bg-hover); }
  .gb-suggestion.stress { border-color:var(--amber-dim); }
  .gb-suggestion.stress:hover { border-color:var(--amber); }
  .gb-suggestion-text { font-size:11px; color:var(--text); flex:1; line-height:1.4; }
  .gb-suggestion-tag { font-size:9px; padding:2px 5px; border-radius:2px; white-space:nowrap; flex-shrink:0; }
  .gb-suggestion-tag.gov{background:var(--blue-dim);color:var(--blue);} .gb-suggestion-tag.stress{background:var(--amber-dim);color:var(--amber);}
  .gb-turns { display:flex; flex-direction:column; gap:12px; margin-bottom:16px; }
  .gb-turn-q { background:var(--bg-card); border:1px solid var(--border); border-radius:4px; padding:10px 12px; }
  .gb-turn-q-label { font-size:9px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:6px; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
  .gb-turn-q-model { color:var(--purple); font-size:9px; }
  .gb-turn-q-text { font-size:12px; color:var(--text); line-height:1.5; }
  .gb-answer-card { border:1px solid var(--green); border-left:3px solid var(--green); border-radius:4px; overflow:hidden; margin-top:8px; }
  .gb-answer-receipt { background:var(--green-dim); padding:8px 12px; display:flex; align-items:flex-start; justify-content:space-between; gap:8px; flex-wrap:wrap; }
  .gb-answer-receipt-meta { display:flex; flex-direction:column; gap:3px; }
  .gb-answer-receipt-title { font-size:9px; color:var(--green); text-transform:uppercase; letter-spacing:0.1em; font-weight:600; }
  .gb-answer-receipt-line { font-size:9px; color:rgba(63,185,80,0.8); font-family:var(--mono); }
  .gb-answer-receipt-actions { display:flex; gap:6px; flex-shrink:0; }
  .gb-receipt-btn { font-size:9px; padding:3px 8px; background:none; border:1px solid rgba(63,185,80,0.4); border-radius:3px; color:var(--green); font-family:var(--mono); cursor:pointer; transition:all 0.15s; white-space:nowrap; }
  .gb-receipt-btn:hover { background:rgba(63,185,80,0.15); border-color:var(--green); }
  .gb-receipt-btn.active { background:rgba(63,185,80,0.2); }
  .gb-answer-text { padding:10px 12px; font-size:12px; color:var(--text); line-height:1.6; font-family:var(--sans); background:var(--bg-card); }
  .gb-answer-evidence { padding:10px 12px; background:var(--bg); border-top:1px solid var(--border); }
  .gb-answer-evidence-title { font-size:9px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:8px; }
  .gb-turn-reject { background:var(--bg-card); border:1px solid var(--red-dim); border-left:2px solid var(--red); border-radius:4px; padding:10px 12px; margin-top:8px; }
  .gb-turn-reject-label { font-size:9px; color:var(--red); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:6px; }
  .gb-turn-reject-text { font-size:11px; color:var(--text-dim); }
  .gb-turn-pending { background:var(--bg-card); border:1px solid var(--border); border-radius:4px; padding:10px 12px; color:var(--text-dim); font-size:11px; display:flex; align-items:center; gap:8px; margin-top:8px; }
  .gb-spinner { width:10px; height:10px; border:1.5px solid var(--border-hi); border-top-color:var(--blue); border-radius:50%; animation:spin 0.8s linear infinite; flex-shrink:0; }
  @keyframes spin{to{transform:rotate(360deg);}}
  .gb-input-area { padding:12px 14px; border-top:1px solid var(--border); display:flex; gap:8px; flex-shrink:0; }
  .gb-input { flex:1; background:var(--bg-card); border:1px solid var(--border); border-radius:4px; padding:8px 10px; color:var(--text); font-family:var(--mono); font-size:12px; resize:none; outline:none; transition:border-color 0.15s; min-height:56px; }
  .gb-input:focus { border-color:var(--border-hi); }
  .gb-input::placeholder { color:var(--text-muted); }
  .gb-send-btn { padding:8px 14px; background:var(--blue-dim); border:1px solid var(--blue); border-radius:4px; color:var(--blue); font-family:var(--mono); font-size:11px; cursor:pointer; transition:all 0.15s; align-self:flex-end; white-space:nowrap; }
  .gb-send-btn:hover { background:var(--blue); color:var(--bg); }
  .gb-send-btn:disabled { opacity:0.4; cursor:not-allowed; }
  .gb-insight-bar { margin-top:6px; }
  .gb-insight-bar-header { display:flex; align-items:center; gap:6px; margin-bottom:5px; }
  .gb-insight-strength { font-size:10px; font-weight:600; padding:1px 6px; border-radius:2px; border:1px solid; }
  .gb-insight-cols { display:flex; gap:4px; }
  .gb-insight-col { flex:1; text-align:center; }
  .gb-insight-tower { height:24px; background:var(--bg); border-radius:2px; overflow:hidden; display:flex; flex-direction:column; justify-content:flex-end; margin-bottom:2px; }
  .gb-insight-fill { border-radius:2px 2px 0 0; transition:height 0.5s ease; }
  .gb-commit-badge { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border-radius:4px; font-size:11px; font-weight:500; }
  .gb-commit-badge.COMMIT{background:var(--green-dim);color:var(--green);border:1px solid var(--green);}
  .gb-commit-badge.REJECT{background:var(--red-dim);color:var(--red);border:1px solid var(--red);}
  .gb-commit-badge.DEFER{background:var(--amber-dim);color:var(--amber);border:1px solid var(--amber);}
  .gb-written-confirm { font-size:10px; color:var(--green); padding:5px 8px; background:var(--green-dim); border-radius:3px; margin-top:6px; display:flex; align-items:center; gap:6px; }
  .gb-modal-overlay{position:fixed;inset:0;background:rgba(8,11,15,0.85);display:flex;align-items:center;justify-content:center;z-index:1000;backdrop-filter:blur(4px);}
  .gb-modal{background:var(--bg-panel);border:1px solid var(--border-hi);border-radius:6px;padding:32px;max-width:480px;width:90%;}
  .gb-modal-title{font-family:var(--sans);font-size:18px;font-weight:700;color:var(--text);margin-bottom:8px;}
  .gb-modal-sub{font-size:11px;color:var(--text-dim);line-height:1.7;margin-bottom:20px;}
  .gb-modal-what{background:var(--bg-card);border:1px solid var(--border);border-radius:4px;padding:12px;margin-bottom:20px;}
  .gb-modal-what-title{font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;}
  .gb-modal-what-item{font-size:11px;color:var(--text);padding:3px 0;display:flex;gap:8px;align-items:flex-start;}
  .gb-modal-what-item::before{content:'-->';color:var(--cyan);flex-shrink:0;}
  .gb-modal-btn-primary{width:100%;padding:12px;background:var(--cyan);border:none;border-radius:4px;color:var(--bg);font-family:var(--mono);font-size:12px;font-weight:600;cursor:pointer;margin-bottom:8px;display:block;text-align:center;text-decoration:none;}
  .gb-modal-btn-secondary{width:100%;padding:8px;background:none;border:1px solid var(--border);border-radius:4px;color:var(--text-muted);font-family:var(--mono);font-size:11px;cursor:pointer;}
  .gb-modal-btn-secondary:hover{border-color:var(--border-hi);color:var(--text);}
  .gb-modal-docket{font-size:9px;color:var(--text-muted);text-align:center;margin-top:12px;letter-spacing:0.06em;}
  .gb-nist-note{font-size:9px;color:var(--text-muted);padding:4px 8px;border-top:1px solid var(--border);margin-top:8px;}
  .gb-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:var(--text-muted);gap:8px;text-align:center;padding:24px;}
  .gb-empty-icon{font-size:32px;opacity:0.3;margin-bottom:4px;}
  .gb-empty-text{font-size:11px;line-height:1.6;max-width:200px;}
  .gb-divider{height:1px;background:var(--border);margin:12px 0;}
  .gb-tag{font-size:9px;padding:1px 5px;border-radius:2px;display:inline-block;}
  .gb-tag.green{background:var(--green-dim);color:var(--green);} .gb-tag.red{background:var(--red-dim);color:var(--red);} .gb-tag.blue{background:var(--blue-dim);color:var(--blue);} .gb-tag.amber{background:var(--amber-dim);color:var(--amber);}
  .gb-chain-ok{font-size:9px;padding:1px 6px;border-radius:2px;background:var(--green-dim);color:var(--green);}
  .gb-legend{background:var(--bg-card);border:1px solid var(--border);border-radius:4px;padding:8px 10px;margin-bottom:12px;}
  .gb-legend-title{font-size:9px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.08em;margin-bottom:7px;}
  .gb-legend-row{display:flex;align-items:flex-start;gap:8px;padding:3px 0;}
  .gb-legend-detail{font-size:9px;color:var(--text-muted);}
`;

const LAYER_DISPLAY = { VULTR:"KERNEL", WIX:"PROTOCOL", MODEL:"LLM", COMMIT_GATE:"COMMIT GATE", LEDGER:"LEDGER", UI:"UI" };
function LayerBadge({ layer, lc }) { return <span className={`gb-layer ${lc}`}>{LAYER_DISPLAY[layer] ?? layer}</span>; }

function ArchLegend() {
  return (
    <div className="gb-legend">
      <div className="gb-legend-title">Architecture layers</div>
      {ARCH_LAYERS.map(({ layer, lc, label, detail }) => (
        <div key={layer} className="gb-legend-row">
          <LayerBadge layer={layer} lc={lc} />
          <div><div style={{ fontSize:10, color:"var(--text)" }}>{label}</div><div className="gb-legend-detail">{detail}</div></div>
        </div>
      ))}
    </div>
  );
}

function HeaderField({ label, value, color }) {
  return (
    <div className="gb-field">
      <span className="gb-field-label">{label}</span>
      <span className={`gb-field-value ${color ?? ""}`}>{value}</span>
    </div>
  );
}

function LifecycleTracker({ currentState, compileStatus, commitDecision, stateTimes }) {
  const currentIdx = LIFECYCLE_STATES.indexOf(currentState);
  return (
    <div className="gb-lifecycle">
      <div className="gb-section-title">Governance Trace</div>
      {LIFECYCLE_STATES.map((state, idx) => {
        const meta = LIFECYCLE_META[state];
        let status = "pending";
        if (idx < currentIdx) status = "done";
        else if (idx === currentIdx) status = "active";
        if (state === "COMPILE_RESULT" && currentIdx >= LIFECYCLE_STATES.indexOf("COMPILE_RESULT"))
          status = compileStatus === "PASS" ? "done" : "fail";
        if (state === "APPENDING" && commitDecision === "REJECT") status = "fail";
        const icon = status==="done"?"✓":status==="active"?"›":status==="fail"?"✕":"·";
        return (
          <div key={state}>
            {state === "APPENDING" && (
              <div className="gb-commit-boundary">
                <div className="gb-commit-boundary-title">Commit boundary</div>
                <div className="gb-commit-boundary-note">Answer only rendered after <strong>ledger_result.written === true</strong> confirmed by server.</div>
              </div>
            )}
            <div className={`gb-state-row ${status}`}>
              <div className="gb-state-body">
                <div className="gb-state-top">
                  <span className="gb-state-icon">{icon}</span>
                  <span className="gb-state-label">{meta.label}</span>
                  <LayerBadge layer={meta.layer} lc={meta.lc} />
                  {stateTimes?.[state] && <span className="gb-state-time">{stateTimes[state]}</span>}
                </div>
                {(status==="active"||status==="done") && <div className="gb-state-note">{meta.note}</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Expandable({ label, badge, badgeType, nist, layer, lc, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="gb-expandable">
      <div className={`gb-expandable-header ${badgeType ?? ""}`} onClick={() => setOpen(o => !o)}>
        <div className="gb-expandable-label">
          <span>{open ? "▾" : "▸"} {label}</span>
          {badge && <span className={`gb-expandable-badge ${badgeType ?? "pending"}`}>{badge}</span>}
          {layer && <LayerBadge layer={layer} lc={lc} />}
          {nist && <span className="gb-expandable-nist">[NIST: {nist}]</span>}
        </div>
      </div>
      {open && <div className="gb-expandable-body">{children}</div>}
    </div>
  );
}

function InsightBar({ signal }) {
  const dims = [
    { key:"actionability", label:"Action" }, { key:"relevance", label:"Relev" },
    { key:"predictive_value", label:"Pred" }, { key:"specificity", label:"Spec" },
    { key:"measurability", label:"Meas" },
  ];
  const scores = dims.map(d => ({ ...d, v: num(signal?.insight_dimensions?.[d.key] ?? signal?.[d.key]) }));
  const minScore = num(signal?.insight_strength ?? signal?.signal_strength) ?? Math.min(...scores.map(s => s.v ?? 0));
  const isStrong = minScore >= 3;
  function barColor(v) { if (v===null) return "var(--border-hi)"; if (v>=4) return "var(--green)"; if (v>=3) return "var(--amber)"; return "var(--red)"; }
  return (
    <div className="gb-insight-bar">
      <div className="gb-insight-bar-header">
        <span style={{ fontSize:9, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em" }}>Insight Strength</span>
        <span className="gb-insight-strength" style={{ background: isStrong?"var(--green-dim)":"var(--red-dim)", color: isStrong?"var(--green)":"var(--red)", borderColor: isStrong?"var(--green)":"var(--red)" }}>
          {minScore !== null ? minScore.toFixed(1) : "—"} / 5
        </span>
      </div>
      <div className="gb-insight-cols">
        {scores.map(({ key, label, v }) => (
          <div key={key} className="gb-insight-col">
            <div className="gb-insight-tower"><div className="gb-insight-fill" style={{ height: v!==null?`${(v/5)*100}%`:"0%", background: barColor(v) }} /></div>
            <div style={{ fontSize:8, color:"var(--text-muted)" }}>{label}</div>
            <div style={{ fontSize:9, color: v>=3?"var(--text)":"var(--red)", fontWeight:500 }}>{v!==null?v:"—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CitationBlock({ citation }) {
  if (!citation) return null;
  return (
    <div className="gb-citation">
      <span style={{ fontSize:9, color:"var(--text-muted)" }}>&#x229F;</span>
      {citation.url
        ? <a className="gb-citation-link" href={citation.url} target="_blank" rel="noopener noreferrer">{citation.title ?? citation.url}</a>
        : <span style={{ fontSize:9, color:"var(--text-dim)" }}>{citation.title}</span>
      }
      {citation.retrieved_utc && <span className="gb-citation-utc">· {citation.retrieved_utc.slice(0,10)}</span>}
    </div>
  );
}

function SubQuestionBlock({ sq, index, signals }) {
  const [open, setOpen] = useState(true);
  const linked = new Set(sq?.linked_signal_ids ?? []);
  const mappedSignals = (signals ?? []).filter(s =>
    linked.size > 0 ? linked.has(s.signal_id) : (s.sub_question_ref === sq.id || s.sub_question_ref === index)
  );
  const sqText = sq?.prompt ?? sq?.question ?? "";
  const ssText = typeof sq?.success_standard === "string" ? sq.success_standard : sq?.success_standard?.definition ?? "";
  const ssSource = typeof sq?.success_standard === "object" ? sq?.success_standard?.framework_source : null;
  return (
    <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:4, marginBottom:8, overflow:"hidden" }}>
      <div style={{ padding:"7px 10px", display:"flex", alignItems:"flex-start", gap:8, cursor:"pointer", userSelect:"none" }} onClick={() => setOpen(o=>!o)}>
        <span style={{ fontSize:9, color:"var(--text-muted)", marginTop:1, flexShrink:0 }}>SQ{index+1}</span>
        <span style={{ fontSize:11, color:"var(--text)", flex:1, lineHeight:1.4 }}>{sqText}</span>
        <span style={{ fontSize:10, color:"var(--text-muted)", flexShrink:0 }}>{open?"▾":"▸"}</span>
      </div>
      {open && (
        <div style={{ padding:"0 10px 10px", borderTop:"1px solid var(--border)" }}>
          {ssText && (
            <div style={{ marginTop:8 }}>
              <div style={{ fontSize:9, color:"var(--cyan)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>
                Success Standard <span style={{ color:"var(--text-muted)", textTransform:"none" }}>[NIST: MEASURE 2.3]</span>
              </div>
              <div style={{ fontSize:10, color:"var(--text-dim)", lineHeight:1.5, padding:"5px 8px", background:"var(--bg-card)", borderRadius:3, borderLeft:"2px solid var(--cyan)" }}>{ssText}</div>
              {ssSource && <CitationBlock citation={{ title: ssSource.name, url: ssSource.url, retrieved_utc: ssSource.retrieved_utc }} />}
            </div>
          )}
          {mappedSignals.length > 0 && (
            <div style={{ marginTop:10 }}>
              <div style={{ fontSize:9, color:"var(--blue)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Signals / System Inputs</div>
              {mappedSignals.map((sig, si) => (
                <div key={si} className="gb-signal-card">
                  <div className="gb-signal-header">
                    <span className="gb-signal-id">{sig.signal_id ?? `sig_${si+1}`}</span>
                    {sig.source_system && <span className={`gb-signal-source ${sig.source_system}`}>{sig.source_system}</span>}
                    <span style={{ flex:1 }} />
                    <span style={{ fontSize:9, color:"var(--text-muted)" }}>strength {sig.insight_strength ?? sig.signal_strength ?? "—"}</span>
                  </div>
                  <div className="gb-signal-value">{sig.raw_value}</div>
                  {sig.citation && <CitationBlock citation={sig.citation} />}
                  <InsightBar signal={sig} />
                </div>
              ))}
            </div>
          )}
          {mappedSignals.length === 0 && (
            <div style={{ marginTop:8, fontSize:10, color:"var(--amber)", padding:"5px 8px", background:"var(--amber-dim)", borderRadius:3 }}>
              No signals mapped — stop_reason: INSUFFICIENT_SIGNAL
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DERViewer({ der, rawOutput, normDiff, compileResult, lifecycleState }) {
  if (!der && !rawOutput) return null;
  const currentIdx = LIFECYCLE_STATES.indexOf(lifecycleState ?? "ANSWER_RENDERED");
  const showDER     = currentIdx >= LIFECYCLE_STATES.indexOf("DER_GENERATED");
  const showCompile = currentIdx >= LIFECYCLE_STATES.indexOf("COMPILE_RESULT");
  const showNorm    = currentIdx >= LIFECYCLE_STATES.indexOf("COMMIT_DECISION");
  if (!showDER) return null;
  const isPass = compileResult?.status === "PASS";
  const subQuestions = der?.QUESTION_LOGIC?.sub_questions ?? [];
  const signals = der?.SYSTEM_INPUT?.inputs ?? [];
  return (
    <div className="gb-der-viewer">
      <div className="gb-section-title">Reasoning Record <LayerBadge layer="MODEL" lc="purple" /></div>
      {der?.DECISION_INTENT && (
        <Expandable label="Decision Intent" defaultOpen badge="ANCHORED" badgeType="info" nist="GOVERN 1.2" layer="VULTR" lc="cyan">
          <div style={{ fontSize:11, color:"var(--text)", lineHeight:1.5, marginBottom:6 }}>{der.DECISION_INTENT.intent_statement}</div>
          {der.DECISION_INTENT.context_framing && <div style={{ fontSize:10, color:"var(--text-dim)", lineHeight:1.4 }}>{der.DECISION_INTENT.context_framing}</div>}
          <div style={{ display:"flex", gap:8, marginTop:6, flexWrap:"wrap" }}>
            {der.DECISION_INTENT.role && <span style={{ fontSize:9, color:"var(--text-muted)", border:"1px solid var(--border)", padding:"1px 5px", borderRadius:2 }}>role: {der.DECISION_INTENT.role}</span>}
            {der.DECISION_INTENT.tier && <span style={{ fontSize:9, color:"var(--text-muted)", border:"1px solid var(--border)", padding:"1px 5px", borderRadius:2 }}>tier: {der.DECISION_INTENT.tier}</span>}
            {der.DECISION_INTENT.impact_domain && <span style={{ fontSize:9, color:"var(--amber)", border:"1px solid var(--amber-dim)", padding:"1px 5px", borderRadius:2 }}>{der.DECISION_INTENT.impact_domain}</span>}
          </div>
        </Expandable>
      )}
      {der?.QUESTION_LOGIC?.strategic_question && (
        <Expandable label="Strategic Question" defaultOpen badge="MAPPED" badgeType="info" layer="MODEL" lc="purple">
          <div style={{ fontSize:11, color:"var(--text)", lineHeight:1.5 }}>{der.QUESTION_LOGIC.strategic_question}</div>
        </Expandable>
      )}
      {subQuestions.length > 0 && (
        <div className="gb-expandable" style={{ marginBottom:6, border:"1px solid var(--border-hi)" }}>
          <div className="gb-expandable-header info" style={{ cursor:"default" }}>
            <div className="gb-expandable-label">
              Sub-Questions + Signals + Citations
              <span className="gb-expandable-badge pending">{subQuestions.length} questions · {signals.length} signals</span>
              <LayerBadge layer="MODEL" lc="purple" />
              <span className="gb-expandable-nist">[NIST: MEASURE 2.3]</span>
            </div>
          </div>
          <div className="gb-expandable-body">
            {subQuestions.map((sq, i) => <SubQuestionBlock key={sq.id ?? i} sq={sq} index={i} signals={signals} />)}
          </div>
        </div>
      )}
      {der?.ILJO && (
        <Expandable label="ILJO — Governed reasoning path" defaultOpen nist="GOVERN 1.2" layer="MODEL" lc="purple">
          {/* CHANGE 2 — GOVERNED_ANSWER added, OUTCOME→cyan, GOVERNED_ANSWER→green */}
          {["INTENT","LOGIC","JUDGMENT","OUTCOME","GOVERNED_ANSWER"].map(field => {
            if (!der.ILJO[field]) return null;
            const labelColor = field === "GOVERNED_ANSWER"
              ? "var(--green)"
              : field === "OUTCOME"
              ? "var(--cyan)"
              : "var(--text-muted)";
            const borderColor = field === "GOVERNED_ANSWER"
              ? "var(--green)"
              : field === "OUTCOME"
              ? "var(--cyan)"
              : "var(--border)";
            return (
              <div key={field} style={{ marginBottom:8 }}>
                <div style={{ fontSize:9, color:labelColor, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>{field}</div>
                <div style={{ fontSize:10, color:"var(--text-dim)", lineHeight:1.5, paddingLeft:8, borderLeft:`1px solid ${borderColor}` }}>
                  {der.ILJO[field]}
                </div>
              </div>
            );
          })}
        </Expandable>
      )}
      {showNorm && normDiff && (
        <Expandable label="Normalization diff" nist="MAP 1.1" layer="VULTR" lc="cyan">
          <div style={{ fontSize:10, color:"var(--text-dim)" }}>
            {normDiff.whitespace_only ? "Whitespace / key ordering only — no semantic changes" : `Changed fields: ${normDiff.changed_fields?.join(", ") ?? "none"}`}
          </div>
        </Expandable>
      )}
      {showCompile && compileResult?.status && (
        <Expandable label={`Compile gate — ${isPass?"PASS":"COMPILE_ERROR"}`} badge={isPass?"PASS":"FAIL"} badgeType={isPass?"pass":"fail"} defaultOpen={!isPass} nist="MEASURE 2.5" layer="WIX" lc="amber">
          {isPass
            ? <div style={{ fontSize:10, color:"var(--green)" }}>All required blocks present · Anchors verified · Signal scoring valid</div>
            : <div className="gb-error-list">{compileResult.errors?.map((e,i)=><div key={i} className="gb-error-item">{e}</div>)}</div>
          }
        </Expandable>
      )}
      {rawOutput && (
        <Expandable label="Raw model output (pre-normalization)" badge="PRE-NORM" badgeType="pending" nist="MEASURE 2.6" layer="MODEL" lc="purple">
          <div className="gb-json">{rawOutput}</div>
        </Expandable>
      )}
    </div>
  );
}

function RAIScorecard({ result }) {
  const [showExplainer, setShowExplainer] = useState(false);
  const raw       = resolveMetrics(result);
  const metrics   = metricAliases(raw ?? {});
  const threshold = num(metrics.mint_min_rai, 0.86);
  const rai       = num(metrics.RAI);
  const delta     = thresholdDelta(rai, threshold);
  const limiter   = limitingComponent(metrics);
  const isPass    = rai !== null && rai >= threshold;
  const components = [
    { key:"correctness", label:"Contract Correctness", weight:0.30, nist:"MEASURE 2.5" },
    { key:"ego_der_gca", label:"EGO / DER",  weight:0.25, nist:"MEASURE 2.6" },
    { key:"iljo",        label:"ILJO",        weight:0.25, nist:"GOVERN 1.2" },
    { key:"superego",    label:"Superego",    weight:0.20, nist:"MANAGE 2.2" },
  ];
  const v2 = [
    { key:"der_schema_S",                    label:"DER Schema",  nist:"MAP 1.1" },
    { key:"gca_score",                       label:"GCA Score",   nist:"GOVERN 1.2" },
    { key:"decision_determinism_S",          label:"Determinism", nist:null },
    { key:"subquestion_verdict_readiness_S", label:"SubQ Ready",  nist:null },
  ];
  function barClass(v) { return v===null?"neutral":v>=0.8?"high":v>=0.5?"med":"low"; }
  if (!raw) {
    return (
      <div className="gb-rai">
        <div className="gb-section-title">RAI — Reasoning Alignment Index <LayerBadge layer="VULTR" lc="cyan" /></div>
        <div style={{ fontSize:10, color:"var(--text-muted)", padding:"8px 0" }}>Awaiting governed turn...</div>
      </div>
    );
  }
  return (
    <div className="gb-rai">
      <div className="gb-section-title">
        RAI — Reasoning Alignment Index
        <LayerBadge layer="VULTR" lc="cyan" />
        {metrics.rai_version && <span className="gb-rai-version">{metrics.rai_version}</span>}
        <span style={{ marginLeft:"auto", fontSize:9, color:"var(--blue)", cursor:"pointer" }} onClick={()=>setShowExplainer(o=>!o)}>What is this?</span>
      </div>
      {showExplainer && (
        <div className="gb-rai-explainer">
          <strong>RAI</strong> — How well this DER conforms to the governance contract at the commit boundary. Weighted sum of four component scores. Range 0-1.<br/>
          <strong>Jc</strong> — How much governed reasoning work was minted this turn. Higher = more structured, cited, bounded work committed to ledger.<br/>
          <div className="gb-rai-rule">Commit rule: RAI {">"}= {threshold} AND ledger write succeeds → answer rendered.</div>
        </div>
      )}
      <div className="gb-rai-gauge">
        <div className={`gb-rai-number ${rai===null?"neutral":isPass?"pass":"fail"}`}>{rai!==null?rai.toFixed(4):"—"}</div>
        <div className="gb-rai-bar-wrap">
          <div className="gb-rai-bar-track">
            <div className={`gb-rai-bar-fill ${rai===null?"neutral":isPass?"pass":"fail"}`} style={{ width: rai!==null?`${Math.min(rai*100,100)}%`:"0%" }} />
            <div className="gb-rai-bar-threshold" style={{ left:`${threshold*100}%` }} />
          </div>
          <div className={`gb-rai-delta ${delta===null?"neutral":delta>=0?"pos":"neg"}`}>
            {delta!==null?`${delta>=0?"+":""}${delta.toFixed(4)} vs threshold ${threshold}`:`threshold ${threshold}`}
          </div>
          <div className="gb-rai-meta">Jc: {num(metrics.Jc)!==null?num(metrics.Jc).toFixed(4):"—"}{" · "}{isPass?"GATE: PASS":rai!==null?"GATE: FAIL":"GATE: PENDING"}</div>
        </div>
      </div>
      <div style={{ marginBottom:8 }}>
        {components.map(({ key, label, weight, nist }) => {
          const v = num(metrics[key]);
          const isLimiter = limiter?.name === key;
          return (
            <div key={key} className="gb-component-row">
              <div className={`gb-component-name ${isLimiter?"limiter":""}`}>{label} x{weight}</div>
              <div className="gb-component-bar"><div className={`gb-component-fill ${barClass(v)}`} style={{ width: v!==null?`${v*100}%`:"0%" }} /></div>
              <div className="gb-component-val">{v!==null?v.toFixed(2):"—"}</div>
              <div className="gb-component-nist">[{nist}]</div>
            </div>
          );
        })}
      </div>
      {limiter && <div className="gb-limiter-callout">Limiter: {limiter.name} ({limiter.value.toFixed(4)}) — lowest component</div>}
      <div className="gb-divider" />
      <div style={{ marginBottom:4, fontSize:10, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em" }}>RAI_v2 Metrics</div>
      {v2.map(({ key, label, nist }) => {
        const v = num(metrics[key]);
        return (
          <div key={key} className="gb-component-row">
            <div className="gb-component-name">{label}</div>
            <div className="gb-component-bar"><div className={`gb-component-fill ${barClass(v)}`} style={{ width: v!==null?`${v*100}%`:"0%" }} /></div>
            <div className="gb-component-val">{v!==null?v.toFixed(2):"—"}</div>
            {nist?<div className="gb-component-nist">[{nist}]</div>:<div className="gb-component-nist"/>}
          </div>
        );
      })}
      <div className="gb-nist-note">NIST AI RMF references shown for alignment visibility. SDI is an early-stage governance runtime.</div>
    </div>
  );
}

function AnswerCard({ turn }) {
  const [showEvidence, setShowEvidence] = useState(false);
  const metrics   = metricAliases(resolveMetrics(turn.result) ?? {});
  const rai       = num(metrics.RAI);
  const jc        = num(metrics.Jc);
  const seq       = turn.result?.ledger_result?.seq ?? turn.result?.ENTRY?.seq ?? "—";
  const entryHash = turn.result?.ledger_result?.entry_hash ?? turn.result?.ENTRY?.entry_hash ?? "";
  const hashPrefix = entryHash ? entryHash.slice(0, 18) + "…" : "—";
  const der       = resolveDER(turn.result);
  const anchors   = der?.GCA?.SUPEREGO?.anchors_present ?? [];
  const signals   = der?.SYSTEM_INPUT?.inputs ?? [];
  return (
    <div className="gb-answer-card">
      <div className="gb-answer-receipt">
        <div className="gb-answer-receipt-meta">
          <div className="gb-answer-receipt-title">Committed Answer</div>
          <div className="gb-answer-receipt-line">seq {seq} · RAI {rai!==null?rai.toFixed(4):"—"} · Jc {jc!==null?jc.toFixed(2):"—"} · {hashPrefix}</div>
          {anchors.length > 0 && <div className="gb-answer-receipt-line">Anchors: {anchors.join(" · ")}</div>}
        </div>
        <div className="gb-answer-receipt-actions">
          {signals.length > 0 && (
            <button className={`gb-receipt-btn ${showEvidence?"active":""}`} onClick={() => setShowEvidence(o=>!o)}>Evidence ({signals.length})</button>
          )}
        </div>
      </div>
      <div className="gb-answer-text">{turn.answer}</div>
      {showEvidence && signals.length > 0 && (
        <div className="gb-answer-evidence">
          <div className="gb-answer-evidence-title">Evidence ({signals.length} signals)</div>
          {signals.map((sig, i) => (
            <div key={i} className="gb-signal-card">
              <div className="gb-signal-header">
                <span className="gb-signal-id">{sig.signal_id}</span>
                {sig.source_system && <span className={`gb-signal-source ${sig.source_system}`}>{sig.source_system}</span>}
                <span style={{ flex:1 }} />
                <span style={{ fontSize:9, color:"var(--text-muted)" }}>strength {sig.insight_strength ?? sig.signal_strength ?? "—"}</span>
              </div>
              <div className="gb-signal-value">{sig.raw_value}</div>
              {sig.citation && <CitationBlock citation={sig.citation} />}
              <InsightBar signal={sig} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LedgerArtifact({ entry, onLoad }) {
  if (!entry) return null;

  useEffect(() => {
    if ((entry._loaded === false || (entry._loaded === true && entry.der === null && entry.type === "EPISODIC")) && entry.seq && onLoad && entry.type !== "system" && entry.type !== "GENESIS" && entry.type !== "POST_MINT_CONTINUITY") {
      if (entry._loaded === true && entry.der !== null) return;
      fetch(LEDGER_GET_URL(entry.seq))
        .then(r => r.json())
        .then(data => {
          const e = data?.event?.ENTRY ?? {};
          const m = data?.event?.AUDIT?.metrics ?? null;
          const der = e?.meta?.SDI_DER ?? null;
          const raw = e?.meta?.model;
          const model_provider = raw
            ? (raw === "gemini" || raw.startsWith("gemini-") ? `google/${raw === "gemini" ? "gemini-2.5-flash" : raw}`
             : raw === "openai" || raw.startsWith("gpt-")   ? `openai/${raw === "openai" ? "gpt-4.1" : raw}`
             : raw.includes("/") ? raw : `anthropic/${raw}`)
            : entry.model_provider;
          onLoad(entry.seq, {
            intent: der?.ILJO?.INTENT ?? entry.intent,
            der,
            metrics: m,
            rai: m?.RAI ?? entry.rai,
            jc:  m?.Jc_minted ?? entry.jc,
            verdict: m?.RAI >= 0.86 ? "PASS" : "FAIL",
            model_provider,
            _loaded: true,
          });
        })
        .catch(() => onLoad(entry.seq, { _loaded: true }));
    }
  }, [entry.seq, entry._loaded, entry.der]);

  const der     = entry.der ?? entry?.ENTRY?.meta?.SDI_DER ?? null;
  const signals = der?.SYSTEM_INPUT?.inputs ?? [];
  const subQs   = der?.QUESTION_LOGIC?.sub_questions ?? [];
  const anchors = der?.GCA?.SUPEREGO?.anchors_present ?? [];
  const metrics = entry.metrics ? metricAliases(entry.metrics) : null;
  const isSystem = entry.type === "GENESIS" || entry.type === "POST_MINT_CONTINUITY";

  return (
    <div className="gb-log-artifact">
      <div className="gb-artifact-section">
        <div className="gb-artifact-label">
          <LayerBadge layer="LEDGER" lc="green" />
          SHA-384 Entry Hash · seq {entry.seq} · {entry.utc}
        </div>
        <div className="gb-artifact-hash-full">{entry.entry_hash}</div>
        {entry.parent_hash && (
          <div style={{ marginTop:4 }}>
            <div className="gb-artifact-label">Parent Hash</div>
            <div className="gb-artifact-hash-full" style={{ color:"var(--text-muted)" }}>{entry.parent_hash}</div>
          </div>
        )}
      </div>

      {isSystem ? (
        <div className="gb-artifact-section">
          <div className="gb-artifact-text">{entry.note}</div>
        </div>
      ) : !der && entry._loaded === false ? (
        <div style={{ fontSize:10, color:"var(--text-muted)", padding:"8px 0" }}>Loading artifact...</div>
      ) : der ? (
        <>
          {der.DECISION_INTENT?.intent_statement && (
            <div className="gb-artifact-section">
              <div className="gb-artifact-label">DECISION_INTENT</div>
              <div className="gb-artifact-text intent">{der.DECISION_INTENT.intent_statement}</div>
              {der.DECISION_INTENT.context_framing && (
                <div className="gb-artifact-text" style={{ marginTop:4, borderLeftColor:"var(--border)" }}>{der.DECISION_INTENT.context_framing}</div>
              )}
              <div style={{ display:"flex", gap:6, marginTop:4, flexWrap:"wrap" }}>
                {der.DECISION_INTENT.role && <span style={{ fontSize:9, color:"var(--text-muted)", border:"1px solid var(--border)", padding:"1px 5px", borderRadius:2 }}>role: {der.DECISION_INTENT.role}</span>}
                {der.DECISION_INTENT.tier && <span style={{ fontSize:9, color:"var(--text-muted)", border:"1px solid var(--border)", padding:"1px 5px", borderRadius:2 }}>tier: {der.DECISION_INTENT.tier}</span>}
                {der.DECISION_INTENT.impact_domain && <span style={{ fontSize:9, color:"var(--amber)", border:"1px solid var(--amber-dim)", padding:"1px 5px", borderRadius:2 }}>{der.DECISION_INTENT.impact_domain}</span>}
              </div>
            </div>
          )}
          {der.QUESTION_LOGIC?.strategic_question && (
            <div className="gb-artifact-section">
              <div className="gb-artifact-label">Strategic Question</div>
              <div className="gb-artifact-text">{der.QUESTION_LOGIC.strategic_question}</div>
            </div>
          )}
          {subQs.length > 0 && (
            <div className="gb-artifact-section">
              <div className="gb-artifact-label"><LayerBadge layer="MODEL" lc="purple" />Sub-Questions + Success Standards ({subQs.length})</div>
              {subQs.map((sq, i) => {
                const ssText   = typeof sq.success_standard === "string" ? sq.success_standard : sq.success_standard?.definition ?? "";
                const ssSource = typeof sq.success_standard === "object" ? sq.success_standard?.framework_source : null;
                return (
                  <div key={sq.id ?? i} style={{ marginBottom:8, padding:"7px 10px", background:"var(--bg)", border:"1px solid var(--border)", borderRadius:3 }}>
                    <div style={{ fontSize:9, color:"var(--text-muted)", marginBottom:3 }}>SQ{i+1} · {sq.id} · linked: {(sq.linked_signal_ids ?? []).join(", ") || "—"}</div>
                    <div style={{ fontSize:10, color:"var(--text)", lineHeight:1.5, marginBottom:4 }}>{sq.prompt ?? sq.question}</div>
                    {ssText && (
                      <div style={{ fontSize:9, color:"var(--text-dim)", padding:"4px 7px", borderLeft:"1px solid var(--cyan)", lineHeight:1.5 }}>
                        <span style={{ color:"var(--cyan)" }}>Success standard: </span>{ssText}
                      </div>
                    )}
                    {ssSource && <CitationBlock citation={{ title:ssSource.name, url:ssSource.url, retrieved_utc:ssSource.retrieved_utc }} />}
                  </div>
                );
              })}
            </div>
          )}
          {signals.length > 0 && (
            <div className="gb-artifact-section">
              <div className="gb-artifact-label"><LayerBadge layer="MODEL" lc="purple" />System Inputs — Signals + Citations ({signals.length})</div>
              {signals.map((sig, i) => (
                <div key={i} className="gb-signal-card">
                  <div className="gb-signal-header">
                    <span className="gb-signal-id">{sig.signal_id}</span>
                    {sig.source_system && <span className={`gb-signal-source ${sig.source_system}`}>{sig.source_system}</span>}
                    <span style={{ flex:1 }} />
                    <span style={{ fontSize:9, color:"var(--text-muted)" }}>strength {sig.insight_strength ?? sig.signal_strength ?? "—"}</span>
                  </div>
                  <div className="gb-signal-value">{sig.raw_value}</div>
                  {sig.provenance && <div style={{ fontSize:9, color:"var(--text-muted)", margin:"3px 0", lineHeight:1.4 }}>Provenance: {sig.provenance}</div>}
                  {sig.citation && <CitationBlock citation={sig.citation} />}
                  <InsightBar signal={sig} />
                </div>
              ))}
            </div>
          )}
          {der.ILJO && (
            <div className="gb-artifact-section">
              <div className="gb-artifact-label"><LayerBadge layer="MODEL" lc="purple" />ILJO — Governed Reasoning Record</div>
              {/* CHANGE 3 — GOVERNED_ANSWER added with safe block-return pattern */}
              {["INTENT","LOGIC","JUDGMENT","OUTCOME","GOVERNED_ANSWER"].map(f => {
                if (!der.ILJO[f]) return null;
                const labelColor = f === "GOVERNED_ANSWER"
                  ? "var(--green)"
                  : f === "OUTCOME"
                  ? "var(--cyan)"
                  : f === "INTENT"
                  ? "var(--cyan)"
                  : "var(--text-muted)";
                const textClass = f === "GOVERNED_ANSWER"
                  ? "gb-artifact-text outcome"
                  : f === "INTENT"
                  ? "gb-artifact-text intent"
                  : "gb-artifact-text";
                return (
                  <div key={f} style={{ marginBottom:7 }}>
                    <div style={{ fontSize:9, color:labelColor, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>{f}</div>
                    <div className={textClass}>{der.ILJO[f]}</div>
                  </div>
                );
              })}
            </div>
          )}
          {anchors.length > 0 && (
            <div className="gb-artifact-section">
              <div className="gb-artifact-label"><LayerBadge layer="VULTR" lc="cyan" />GCA Anchors Present</div>
              <div className="gb-anchor-row">
                {anchors.map(a => <span key={a} className="gb-anchor-pill">{a}</span>)}
                {(der.GCA?.SUPEREGO?.anchors_required ?? []).filter(r => !anchors.includes(r)).map(a => (
                  <span key={a} style={{ fontSize:9, padding:"2px 6px", borderRadius:2, background:"var(--red-dim)", color:"var(--red)", border:"1px solid rgba(248,81,73,0.3)" }}>{a} MISSING</span>
                ))}
              </div>
            </div>
          )}
          {metrics && (
            <div className="gb-artifact-section">
              <div className="gb-artifact-label"><LayerBadge layer="VULTR" lc="cyan" />AUDIT Metrics</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {[
                  ["RAI",    metrics.RAI?.toFixed?.(4) ?? "—"],
                  ["Jc",     metrics.Jc?.toFixed?.(4)  ?? "—"],
                  ["Contract Corr", (num(entry.metrics?.Correctness) ?? num(entry.metrics?.correctness))?.toFixed?.(4) ?? "—"],
                  ["EGO",    num(entry.metrics?.Ego_bucket_S)?.toFixed?.(4) ?? "—"],
                  ["Superego", num(entry.metrics?.Superego_score)?.toFixed?.(4) ?? "—"],
                ].map(([k,v]) => (
                  <span key={k} style={{ fontSize:9, border:"1px solid var(--border)", padding:"2px 6px", borderRadius:2 }}>
                    <span style={{ color:"var(--text-muted)" }}>{k}: </span>
                    <span style={{ color:"var(--green)" }}>{v}</span>
                  </span>
                ))}
              </div>
              {entry.metrics?.rai_version && (
                <div style={{ fontSize:9, color:"var(--text-muted)", marginTop:4 }}>{entry.metrics.rai_version} · threshold: {entry.metrics?.thresholds?.mint_min_rai ?? 0.86}</div>
              )}
            </div>
          )}
        </>
      ) : (
        entry.note && <div className="gb-artifact-text">{entry.note}</div>
      )}
      <div style={{ marginTop:10, padding:"6px 8px", background:"var(--bg-card)", borderRadius:4, fontSize:9, color:"var(--text-muted)", fontFamily:"var(--mono)" }}>
        <span style={{ color:"var(--purple)", marginRight:6 }}>MODEL</span>
        Raw model output (pre-normalization) not stored in ledger — available in live turn panel only. Ledger stores normalized, hash-verified DER record only. <span style={{ color:"var(--text-muted)" }}>NIST MEASURE 2.6</span>
      </div>
    </div>
  );
}

function LedgerDock({ entries, chainOk, isDemo, onLoad }) {
  const lastSeq = entries[entries.length - 1]?.seq;
  const [openSeqs, setOpenSeqs] = useState(() => new Set(lastSeq != null ? [lastSeq] : []));
  useEffect(() => {
    const last = entries[entries.length-1];
    if (last) setOpenSeqs(prev => new Set([...prev, last.seq]));
  }, [entries.length]);
  function toggle(seq) {
    setOpenSeqs(prev => { const next = new Set(prev); next.has(seq) ? next.delete(seq) : next.add(seq); return next; });
  }
  return (
    <div className="gb-ledger-dock">
      <div className="gb-dock-header">
        <LayerBadge layer="LEDGER" lc="green" />
        SHA-384 Hash Chain · {entries.length} entries
        {chainOk && <span className="gb-chain-ok">chain_ok ✓</span>}
        <span style={{ marginLeft:12, fontSize:9, color:"var(--text-muted)", fontStyle:"italic", textTransform:"none", letterSpacing:0 }}>
          Reasoning record — the system holds the governed reasoning history, not the model
        </span>
        <span style={{ marginLeft:"auto", fontSize:9, color:"var(--text-muted)" }}>↓ click entry to examine artifact</span>
      </div>
      {isDemo && (
        <div className="gb-dock-persist">
          ⚠ This is a shared demo agent — questions and reasoning artifacts are public and permanent. Don't submit sensitive information. Committed turns may be used for SDI protocol research.
        </div>
      )}
      <div className="gb-dock-scroll">
        {entries.map(entry => {
          const open = openSeqs.has(entry.seq);
          const intentPreview = (entry.intent ?? entry.note ?? "").slice(0, 80);
          const isHead = entry.seq === entries[entries.length-1].seq;
          return (
            <div key={entry.seq} className={`gb-log-row ${open ? "open" : ""}`}>
              <div className="gb-log-row-head" onClick={() => toggle(entry.seq)}>
                <div className="gb-log-seq">{entry.seq}</div>
                <div className="gb-log-caret">{open ? "▾" : "▸"}</div>
                <div className="gb-log-hash-col">
                  {isHead && <span style={{ fontSize:8, color:"var(--amber)", marginRight:4 }}>HEAD</span>}
                  {entry.entry_hash?.slice(0, 20)}…
                </div>
                <div className="gb-log-type-col">
                  {entry.type}
                  {entry.model_provider && (
                    <span style={{ color:"var(--purple)", marginLeft:6, fontSize:8 }}>
                      {entry.model_provider.startsWith("google/") ? "gemini"
                        : entry.model_provider.startsWith("openai/") ? "openai"
                        : entry.model_provider.split("/")[0]}
                    </span>
                  )}
                </div>
                <div className="gb-log-intent-col">{intentPreview}</div>
                <div className="gb-log-utc-col">{entry.utc?.slice(0,19).replace("T"," ")}</div>
                <div className="gb-log-rai-col">
                  {entry.rai != null
                    ? <span className="gb-log-rai pass">RAI {entry.rai.toFixed(4)}</span>
                    : <span style={{ fontSize:9, color:"var(--text-muted)" }}>—</span>
                  }
                </div>
              </div>
              {open && <LedgerArtifact entry={entry} onLoad={onLoad} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LiveNotice({ onDismiss }) {
  return (
    <div className="gb-notice-overlay">
      <div className="gb-notice">
        <div className="gb-notice-title"><span style={{ fontSize:14 }}>⚠</span> Live Demo — Research Disclosure</div>
        <div className="gb-notice-body">
          This is a <strong>live governed reasoning demo</strong> running on the SDI Protocol.<br/><br/>
          You are interacting with a <strong>shared demo agent</strong>. Questions and reasoning artifacts are committed to a permanent, append-only hash chain that is <strong>public and cumulative</strong> across all demo users.<br/><br/>
          Do not submit sensitive, private, or proprietary information. Committed turns may be used for <strong>SDI protocol research</strong>. The ledger is immutable — entries cannot be deleted.
        </div>
        <button className="gb-notice-confirm" onClick={onDismiss}>I understand — show me the demo</button>
      </div>
    </div>
  );
}

function NISTObserverModal({ onContinue }) {
  return (
    <div className="gb-modal-overlay">
      <div className="gb-modal">
        <div className="gb-modal-title">Three governed turns complete.</div>
        <div className="gb-modal-sub">
          Each answer above was sourced from a hash-chained reasoning artifact committed to the public ledger — not from raw model output. The governance gate executed deterministically: RAI was scored, the threshold was applied, and the ledger was written before any answer was rendered.
        </div>
        <div className="gb-modal-what">
          <div className="gb-modal-what-title">Continue as observer</div>
          <div className="gb-modal-what-item">Scroll the ledger below to examine committed artifacts from all three model providers</div>
          <div className="gb-modal-what-item">Click any entry to inspect the full DER — signals, citations, ILJO reasoning path, GCA anchors, and SHA-384 hashes</div>
          <div className="gb-modal-what-item">Query the live ledger directly at api.sdi-protocol.org/ledger/list/SDI-4EDBE05288CB</div>
        </div>
        <a
          href="https://sdi-protocol.org"
          target="_blank"
          rel="noopener noreferrer"
          className="gb-modal-btn-primary"
        >
          Read the protocol specification — sdi-protocol.org
        </a>
        <button className="gb-modal-btn-secondary" onClick={onContinue}>
          Continue as observer — stay in ledger view
        </button>
        <div className="gb-modal-docket">Provided for NIST review · Docket NIST-2025-0035</div>
      </div>
    </div>
  );
}

function AnthropicGlyph({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      {[0,45,90,135,180,225,270,315].map((angle, i) => (
        <line key={i} x1="12" y1="12"
          x2={12 + 8.5 * Math.cos((angle * Math.PI) / 180)}
          y2={12 + 8.5 * Math.sin((angle * Math.PI) / 180)}
          stroke="#E87A5D" strokeWidth="2.2" strokeLinecap="round" />
      ))}
    </svg>
  );
}

function GeminiGlyph({ size = 14, dimmed }) {
  const color = dimmed ? "#4a5568" : "#8AB4F8";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2 L14.5 9.5 L22 12 L14.5 14.5 L12 22 L9.5 14.5 L2 12 L9.5 9.5 Z" fill={color} opacity={dimmed ? 0.4 : 1} />
    </svg>
  );
}

function OpenAIGlyph({ size = 14, dimmed }) {
  const color = dimmed ? "#4a5568" : "#ffffff";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" opacity={dimmed ? 0.4 : 1}>
      <path d="M12 2.5 C15.5 2.5 18.5 4.8 19.5 8 C21 8.5 22 9.9 22 11.5 C22 13.1 21 14.5 19.5 15 C19 18.2 16.3 20.5 13 20.5 C11.5 20.5 10.1 20 9 19.1 C7.5 19.6 5.8 19.2 4.7 18 C3.6 16.8 3.4 15.1 4 13.7 C3 12.7 2.5 11.3 2.8 9.9 C3.1 8.5 4.1 7.3 5.5 6.8 C6.3 4.3 8.9 2.5 12 2.5Z" stroke={color} strokeWidth="1.4" fill="none"/>
      <circle cx="12" cy="12" r="2.2" fill={color}/>
    </svg>
  );
}

function ModelSelector({ model, onChange, disabled }) {
  return (
    <div className="gb-model-selector">
      <div className="gb-model-selector-label">Select Model Provider</div>
      <div className="gb-model-track">
        <button className={`gb-model-opt ${model === "anthropic" ? "active" : ""}`} onClick={() => !disabled && onChange("anthropic")} disabled={disabled}>
          <AnthropicGlyph size={13} />
          <div><div className="gb-model-opt-name">Anthropic</div><div className="gb-model-opt-version">claude-sonnet-4-6</div></div>
        </button>
        <button className={`gb-model-opt ${model === "gemini" ? "active" : ""}`} onClick={() => !disabled && onChange("gemini")} disabled={disabled}>
          <GeminiGlyph size={13} />
          <div><div className="gb-model-opt-name">Gemini</div><div className="gb-model-opt-version">gemini-2.5-flash</div></div>
        </button>
        <button className={`gb-model-opt ${model === "openai" ? "active" : ""}`} onClick={() => !disabled && onChange("openai")} disabled={disabled}>
          <OpenAIGlyph size={13} />
          <div><div className="gb-model-opt-name">OpenAI</div><div className="gb-model-opt-version">gpt-4.1</div></div>
        </button>
      </div>
    </div>
  );
}

const MAX_DEMO_TURNS = 3;

export default function GlassBox({ session = DEMO_SESSION, onQuestion }) {
  const [model, setModel]                   = useState("anthropic");
  const [question, setQuestion]             = useState("");
  const [turns, setTurns]                   = useState([]);
  const [activeTurn, setActiveTurn]         = useState(null);
  const [lifecycleState, setLifecycleState] = useState(null);
  const [stateTimes, setStateTimes]         = useState({});
  const [chainEntries, setChainEntries]     = useState(GENESIS_ENTRIES);
  const [chainOk, setChainOk]               = useState(true);
  const [showObserver, setShowObserver]     = useState(false);
  const [showNotice, setShowNotice]         = useState(true);
  const [processing, setProcessing]         = useState(false);
  const [currentResult, setCurrentResult]   = useState(null);
  const scrollRef = useRef(null);
  const turnCount = turns.length;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, activeTurn, lifecycleState]);

  useEffect(() => {
    fetch(LEDGER_LIST_URL)
      .then(r => r.json())
      .then(data => {
        const items = data?.items;
        if (!Array.isArray(items) || items.length === 0) return;
        const normalizeProvider = (raw) => {
          if (!raw) return null;
          if (raw.includes("/")) return raw;
          if (raw === "gemini") return "google/gemini-2.5-flash";
          if (raw === "openai") return "openai/gpt-4.1";
          if (raw === "anthropic") return "anthropic/claude-sonnet-4-6";
          return raw;
        };
        const fetched = items.map(item => ({
          seq:            item.seq,
          type:           item.type ?? "EPISODIC",
          model_provider: normalizeProvider(item.model_provider),
          entry_hash:     item.entry_hash,
          parent_hash:    item.parent_hash === "GENESIS" ? null : item.parent_hash,
          utc:            item.utc,
          rai:            null,
          jc:             null,
          verdict:        null,
          intent:         item.intent ?? null,
          der:            null,
          metrics:        null,
          _loaded:        false,
        }));
        setChainEntries(prev => {
          const prevMap = Object.fromEntries(prev.map(e => [e.seq, e]));
          return fetched.map(e => {
            const cached = prevMap[e.seq];
            if (!cached) return e;
            const hasRichData = cached.der != null;
            return { ...e, ...cached, _loaded: hasRichData };
          });
        });
        if (data?.chain_ok !== undefined) setChainOk(data.chain_ok);

        fetched
          .filter(e => e.type === "EPISODIC")
          .forEach(e => {
            fetch(LEDGER_GET_URL(e.seq))
              .then(r => r.json())
              .then(data => {
                const entry      = data?.event?.ENTRY ?? {};
                const m          = data?.event?.AUDIT?.metrics ?? null;
                const der        = entry?.meta?.SDI_DER ?? null;
                const raw        = entry?.meta?.model;
                const model_provider = raw
                  ? (raw === "gemini"  ? "google/gemini-2.5-flash"
                   : raw === "openai"  ? "openai/gpt-4.1"
                   : raw.includes("/") ? raw
                   : `anthropic/${raw}`)
                  : e.model_provider;
                setChainEntries(prev => prev.map(ce =>
                  ce.seq === e.seq
                    ? { ...ce,
                        rai:            m?.RAI ?? ce.rai,
                        jc:             m?.Jc_minted ?? ce.jc,
                        metrics:        m ?? ce.metrics,
                        der:            der ?? ce.der,
                        intent:         der?.ILJO?.INTENT ?? ce.intent,
                        model_provider,
                        _loaded:        true }
                    : ce
                ));
              })
              .catch(() => {});
          });
      })
      .catch(() => {
        fetch(LEDGER_HEAD_URL)
          .then(r => r.json())
          .then(data => { if (data?.chain_ok !== undefined) setChainOk(data.chain_ok); })
          .catch(() => {});
      });
  }, []);

  const advanceState = useCallback((state, result) => {
    setLifecycleState(state);
    setStateTimes(prev => ({
      ...prev,
      [state]: new Date().toLocaleTimeString("en-US", { hour12:false, hour:"2-digit", minute:"2-digit", second:"2-digit" })
    }));
    if (result) setCurrentResult(result);
  }, []);

  const handleEntryLoad = useCallback((seq, patch) => {
    setChainEntries(prev => prev.map(e => e.seq === seq ? { ...e, ...patch } : e));
  }, []);

  const submitQuestion = useCallback(async (q) => {
    if (!q.trim() || processing) return;
    if (turnCount >= MAX_DEMO_TURNS) { setShowObserver(true); return; }
    const q_text = q.trim();
    setQuestion("");
    setProcessing(true);
    setCurrentResult(null);
    setStateTimes({});
    const turnId = `turn-${Date.now()}`;
    setActiveTurn({ question: q_text, turnId, model });

    const simulate = async () => {
      advanceState("QUESTION_RECEIVED");
      await delay(300);
      advanceState("SHELL_PROCESSING");
      await delay(800);
      advanceState("DER_GENERATED");

      let result = null;
      if (onQuestion) {
        try { result = await onQuestion({ question: q_text, model, turnId, session, turnCount }); }
        catch (e) { console.error("Shell error:", e); }
      } else {
        result = mockTurnResult(q_text, model, turnId, session, turnCount + 4);
      }

      advanceState("COMPILE_RUNNING");
      await delay(400);
      advanceState("COMPILE_RESULT", result);
      await delay(300);
      advanceState("NORMALIZING");
      await delay(300);
      advanceState("COMMIT_DECISION");
      await delay(400);

      const commitDecision    = result?.commit_decision;
      const writtenConfirmed  = onQuestion ? (result?.ledger_result?.written === true) : true;
      const isCommit          = commitDecision === "COMMIT" && writtenConfirmed;

      advanceState("APPENDING");
      await delay(500);

      if (isCommit) {
        advanceState("CHAIN_VERIFIED");
        await delay(200);
        const resolvedMetrics = resolveMetrics(result);
        setChainEntries(prev => {
          const der = resolveDER(result);
          return [...prev, {
            seq:            result?.ledger_result?.seq ?? (prev.length + 1),
            type:           "EPISODIC",
            model_provider: result?.model_provider ?? (model==="anthropic"?"anthropic/claude-sonnet-4-6":model==="gemini"?"google/gemini-2.5-flash":"openai/gpt-4.1"),
            entry_hash:     result?.ledger_result?.entry_hash ?? `0x${Array.from({length:96},()=>Math.floor(Math.random()*16).toString(16)).join("")}`,
            parent_hash:    result?.ledger_result?.parent_hash ?? prev[prev.length - 1]?.entry_hash ?? null,
            utc:            new Date().toISOString(),
            rai:            resolvedMetrics?.RAI ?? null,
            jc:             resolvedMetrics?.Jc_minted ?? null,
            verdict:        "COMMIT",
            intent:         der?.ILJO?.INTENT ?? q_text,
            question:       q_text,
            outcome:        der?.ILJO?.OUTCOME ?? null,
            der:            der,
            metrics:        resolvedMetrics,
            _loaded:        true,
          }];
        });
        setChainOk(result?.chain_verify?.chain_ok ?? true);
      }

      advanceState("ANSWER_RENDERED");
      const der = resolveDER(result);
      // CHANGE 4 — governed_answer preferred over answer and OUTCOME
      const answer = isCommit
        ? (result?.governed_answer ?? result?.answer ?? der?.ILJO?.GOVERNED_ANSWER ?? der?.ILJO?.OUTCOME ?? extractAnswer(result))
        : null;

      setTurns(prev => [...prev, { question: q_text, model, turnId, result, answer, committed: isCommit }]);
      setActiveTurn(null);
      setProcessing(false);
      if (turnCount + 1 >= MAX_DEMO_TURNS) { await delay(1500); setShowObserver(true); }
    };

    simulate();
  }, [processing, turnCount, model, session, advanceState, onQuestion]);

  function handleSuggestion(text) {
    if (!processing && turnCount < MAX_DEMO_TURNS) submitQuestion(text);
  }

  return (
    <>
      <style>{css}</style>
      <div className="gb-root">
        <div className="gb-header">
          <div className="gb-logo-wrap">
            <img
              className="gb-logo-img"
              src="https://static.wixstatic.com/media/5b8edb_52136950a4c9482ca216c51e1b91a1fa~mv2.png"
              alt="SDI"
              onError={e => { e.target.style.display="none"; }}
            />
            <div>
              <div className="gb-logo-text">SDI GLASS BOX</div>
              <div className="gb-logo-sub">Structured Decision Intelligence</div>
            </div>
          </div>
          <div className="gb-header-fields">
            <div className="gb-dot" />
            <HeaderField label="AGENT"    value={session.agent_id}         color="cyan" />
            <HeaderField label="CONTRACT" value={session.contract_version} color="blue" />
            <HeaderField label="SOURCE"   value={session.contract_source}  color="dim" />
            <HeaderField label="CHAIN"    value={chainOk?"✓ OK":"✕ ERR"}  color={chainOk?"green":"red"} />
            <HeaderField label="POLICY"   value={session.policy_mode}      color="amber" />
            <a href="https://sdi-protocol.org" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none" }}>
              <HeaderField label="PROTOCOL" value="sdi-protocol.org ↗" color="cyan" />
            </a>
            <a href="https://github.com/StructuredDecisionIntelligence" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none" }}>
              <HeaderField label="OPEN SOURCE" value="github ↗" color="purple" />
            </a>
          </div>
          <ModelSelector model={model} onChange={setModel} disabled={processing} />
          <div className="gb-turn-counter"><span>{turnCount}</span> / {MAX_DEMO_TURNS} demo turns</div>
        </div>

        <div className="gb-body">
          <div className="gb-main">
            <div className="gb-pane">
              <div className="gb-pane-header">
                Conversation
                <div className="gb-pane-header-right"><LayerBadge layer="UI" lc="blue" /></div>
              </div>
              <div className="gb-pane-scroll" ref={scrollRef}>
                <div className="gb-mint-story">
                  <div className="gb-mint-story-title">Agent Genesis</div>
                  <div className="gb-mint-story-text">Minted via governed challenge-verify-mint. Identity is sovereign. Memory hash-chained from genesis.</div>
                  {[["Agent ID", session.agent_id],["Sov. Hash",`${session.sovereign_hash?.slice(0,20)}…`],["Contract",session.contract_version],["Ledger",`Append-only · seq 1–${chainEntries.length}`]].map(([k,v])=>(
                    <div key={k} className="gb-mint-story-field">
                      <span className="gb-mint-story-key">{k}</span>
                      <span className="gb-mint-story-val">{v}</span>
                    </div>
                  ))}
                </div>
                {turnCount === 0 && !processing && (
                  <div className="gb-suggestions">
                    <div className="gb-suggestions-label">Try a question</div>
                    {SUGGESTED_QUESTIONS.map((sq,i)=>(
                      <button key={i} className={`gb-suggestion ${sq.stress?"stress":""}`} onClick={()=>handleSuggestion(sq.text)}>
                        <span className="gb-suggestion-text">{sq.text}</span>
                        <span className={`gb-suggestion-tag ${sq.stress?"stress":"gov"}`}>{sq.tag}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="gb-turns">
                  {turns.map((turn, i) => (
                    <div key={turn.turnId}>
                      <div className="gb-turn-q">
                        <div className="gb-turn-q-label">
                          Turn {i+1}
                          <span className="gb-turn-q-model">{turn.model}</span>
                          {turn.committed ? <span className="gb-tag green">COMMITTED</span> : <span className="gb-tag red">REJECTED</span>}
                        </div>
                        <div className="gb-turn-q-text">{turn.question}</div>
                      </div>
                      {turn.answer
                        ? <AnswerCard turn={turn} />
                        : <div className="gb-turn-reject">
                            <div className="gb-turn-reject-label">Governed Refusal — REJECT</div>
                            <div className="gb-turn-reject-text">{turn.result?.failure?.reason ?? "DER did not meet governance threshold. Ledger write blocked."}</div>
                          </div>
                      }
                    </div>
                  ))}
                  {activeTurn && (
                    <div>
                      <div className="gb-turn-q">
                        <div className="gb-turn-q-label">Turn {turnCount+1} <span className="gb-turn-q-model">{activeTurn.model}</span> <span className="gb-tag blue">PROCESSING</span></div>
                        <div className="gb-turn-q-text">{activeTurn.question}</div>
                      </div>
                      <div className="gb-turn-pending"><div className="gb-spinner" />Governance path executing — answer held until ledger write confirmed…</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="gb-input-area">
                <textarea
                  className="gb-input"
                  placeholder={turnCount >= MAX_DEMO_TURNS
                    ? "Demo complete — scroll the ledger below to examine committed artifacts, or visit sdi-protocol.org"
                    : "Ask a question…"}
                  value={question}
                  onChange={e=>setQuestion(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();submitQuestion(question);}}}
                  disabled={processing||turnCount>=MAX_DEMO_TURNS}
                />
                <button className="gb-send-btn" onClick={()=>submitQuestion(question)} disabled={processing||!question.trim()||turnCount>=MAX_DEMO_TURNS}>Submit →</button>
              </div>
            </div>

            <div className="gb-pane">
              <div className="gb-pane-header">
                <div>
                  Governance Trace
                  <span style={{ marginLeft:8, fontSize:8, color:"var(--text-muted)", textTransform:"none", letterSpacing:0, fontStyle:"italic" }}>
                    steps reflect actual server-side sequence — rendered after response is received
                  </span>
                </div>
                <div className="gb-pane-header-right">
                  <LayerBadge layer="VULTR" lc="cyan" />
                  <LayerBadge layer="WIX" lc="amber" />
                  <LayerBadge layer="MODEL" lc="purple" />
                </div>
              </div>
              <div className="gb-pane-scroll">
                {!lifecycleState && !processing && (
                  <>
                    <ArchLegend />
                    <div className="gb-empty">
                      <div className="gb-empty-icon">⬡</div>
                      <div className="gb-empty-text">Submit a question to watch governance execute in real time.</div>
                    </div>
                  </>
                )}
                {lifecycleState && (
                  <>
                    <LifecycleTracker
                      currentState={lifecycleState}
                      compileStatus={currentResult?.compile_result?.status}
                      commitDecision={currentResult?.commit_decision}
                      stateTimes={stateTimes}
                    />
                    {currentResult && (
                      <>
                        <div className="gb-divider" />
                        <DERViewer
                          der={resolveDER(currentResult)}
                          rawOutput={currentResult?.raw_model_output}
                          normDiff={currentResult?.normalization_diff}
                          compileResult={currentResult?.compile_result}
                          lifecycleState={lifecycleState}
                        />
                        {currentResult?.commit_decision && (
                          <div style={{ marginBottom:12 }}>
                            <div className="gb-section-title" style={{ marginBottom:8 }}>
                              Commit Decision <LayerBadge layer="VULTR" lc="cyan" />
                            </div>
                            <div className={`gb-commit-badge ${currentResult.commit_decision}`}>
                              {currentResult.commit_decision==="COMMIT"?"✓":"✕"} {currentResult.commit_decision}
                            </div>
                            {currentResult.commit_decision==="COMMIT" && (
                              <div className="gb-written-confirm">
                                <LayerBadge layer="LEDGER" lc="green" />
                                written: {String(currentResult?.ledger_result?.written ?? "—")} · seq: {currentResult?.ledger_result?.seq ?? "—"}
                              </div>
                            )}
                            {currentResult?.failure && (
                              <div style={{ marginTop:8, fontSize:10, color:"var(--red)", padding:"6px 10px", background:"var(--red-dim)", borderRadius:3 }}>
                                {currentResult.failure.reason}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="gb-divider" />
                        <RAIScorecard result={currentResult} />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <LedgerDock entries={chainEntries} chainOk={chainOk} isDemo={session?.policy_mode === "DEMO"} onLoad={handleEntryLoad} />
        </div>
      </div>

      {showNotice && <LiveNotice onDismiss={() => setShowNotice(false)} />}
      {showObserver && <NISTObserverModal onContinue={() => setShowObserver(false)} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function delay(ms) { return new Promise(r => setTimeout(r, ms)); }

// CHANGE 5 — governed_answer preferred in fallback chain
function extractAnswer(result) {
  if (!result) return null;
  const der = resolveDER(result);
  return result?.governed_answer ?? result?.answer ?? der?.ILJO?.GOVERNED_ANSWER ?? der?.ILJO?.OUTCOME ?? der?.ILJO?.outcome_statement ?? null;
}

function mockTurnResult(question, model, turnId, session, seq) {
  const provider = model==="anthropic"?"anthropic/claude-sonnet-4-6":model==="gemini"?"google/gemini-2.5-flash":"openai/gpt-4.1";
  const randHash = () => `0x${Array.from({length:96},()=>Math.floor(Math.random()*16).toString(16)).join("")}`;
  return {
    turn_id: turnId,
    agent_id: session.agent_id,
    model_provider: provider,
    lifecycle_state: "ANSWER_RENDERED",
    der: {
      META: { utc: new Date().toISOString(), agent_id: session.agent_id, der_version: "SDI_DER_v1.1" },
      DECISION_INTENT: {
        intent_statement: `Determine the governed position on: ${question.slice(0,90)}`,
        context_framing: "Strategic governance question requiring structured signal decomposition and bounded judgment under SDI protocol.",
        role: "analyst", tier: "Strategic",
      },
      QUESTION_LOGIC: {
        strategic_question: "What measurable conditions and institutional precedents determine the correct governed response?",
        sub_questions: [
          { id:"q1", prompt:"What is the current state of AI governance frameworks across major regulatory bodies?", linked_signal_ids:["sig_001"], success_standard:{ definition:"Governance frameworks from 3+ jurisdictions with measurable adoption status within 24 months.", framework_source:{ name:"NIST AI RMF 1.0", url:"https://airc.nist.gov/AI_RMF_1.0/overview", retrieved_utc: new Date().toISOString() } } },
          { id:"q2", prompt:"What risk categories are unaddressed by existing AI oversight mechanisms?", linked_signal_ids:["sig_002"], success_standard:{ definition:"2+ documented risk categories with evidence of harm potential and absence of current mitigation standard.", framework_source:{ name:"EU AI Act, Title III (2024)", url:"https://artificialintelligenceact.eu/", retrieved_utc: new Date().toISOString() } } },
        ],
      },
      SYSTEM_INPUT: {
        inputs: [
          { signal_id:"sig_001", source_system:"WEB", raw_value:"41% of federal agencies have initiated NIST AI RMF alignment. EU AI Act enforcement begins Q1 2026 with 8 high-risk categories.", captured_utc: new Date().toISOString(), citation:{ title:"NIST AI RMF 1.0 (2023)", url:"https://airc.nist.gov/AI_RMF_1.0/overview", retrieved_utc: new Date().toISOString() }, insight_dimensions:{ actionability:4, relevance:5, predictive_value:3, specificity:4, measurability:4 }, insight_strength:3 },
          { signal_id:"sig_002", source_system:"DOC", raw_value:"~23% factual error rate in unstructured LLM generation without governance gate. Oversight threshold: documented accuracy baseline with periodic revalidation.", captured_utc: new Date().toISOString(), citation:{ title:"Stanford HAI Report 2024", url:"https://hai.stanford.edu/", retrieved_utc: new Date().toISOString() }, insight_dimensions:{ actionability:5, relevance:5, predictive_value:4, specificity:4, measurability:5 }, insight_strength:4 },
        ],
      },
      ILJO: {
        INTENT: question,
        LOGIC: "Two sub-questions decomposed. Two signals mapped: NIST RMF adoption data and documented hallucination rates. Signal scoring applied. BOUNDEDNESS applied.",
        JUDGMENT: "Evidence supports affirmative position with medium-high confidence. SOVEREIGNTY satisfied. PRIMUM passed. STOP_ON_UNCERTAINTY not triggered.",
        OUTCOME: "STATE=ANSWER_COMMITTED",
        GOVERNED_ANSWER: "Governed analysis supports prioritizing AI governance investment before autonomous agent deployment. Two measurable signals — spanning NIST RMF adoption rates and documented hallucination risk — collectively indicate that unstructured AI deployment carries quantifiable and currently unmitigated risk. This reasoning artifact is committed under SDI_DER_v1.1.",
      },
      GCA: {
        SUPEREGO: { anchors_required:["SOVEREIGNTY","PRIMUM","BOUNDEDNESS","STOP_ON_UNCERTAINTY"], anchors_present:["SOVEREIGNTY","PRIMUM","BOUNDEDNESS","STOP_ON_UNCERTAINTY"] },
      },
      BOUNDEDNESS: { uncertainty:"LOW", max_uncertainty_allowed:"MED", timebox_seconds:90, stop_on_uncertainty:true, stop_reason:null },
    },
    normalization_diff: { whitespace_only:true, changed_fields:[] },
    compile_result: { status:"PASS", errors:[] },
    AUDIT: {
      metrics: {
        rai_version:"RAI_v2", RAI:0.94, Jc_minted:8.0,
        Correctness:0.94, Ego_bucket_S:0.90, Superego_score:1.0,
        DER_schema_S:1.0, GCA_score:1.0, Subquestion_verdict_readiness_S:1.0,
        Decision_determinism_S:0.92, Sigma_resistance:1.0, ID_tokens_est:14,
        rai_breakdown:{ iljo:0.25, ego_der_gca:0.225, correctness:0.282, superego:0.2 },
        weights:{ W_ILJO:0.25, W_EGO:0.25, W_CORR:0.30, W_SUP:0.20 },
        thresholds:{ mint_min_rai:0.86, mint_min_jc:0 },
      },
    },
    commit_decision: "COMMIT",
    governed_answer: "Governed analysis supports prioritizing AI governance investment before autonomous agent deployment. Two measurable signals — spanning NIST RMF adoption rates and documented hallucination risk — collectively indicate that unstructured AI deployment carries quantifiable and currently unmitigated risk. This reasoning artifact is committed under SDI_DER_v1.1.",
    ledger_result: { seq, entry_hash:randHash(), parent_hash:randHash(), chain_ok:true, written:true },
    chain_verify: { chain_ok:true, tip_seq:seq },
    answer: "Governed analysis supports prioritizing AI governance investment before autonomous agent deployment. Two measurable signals — spanning NIST RMF adoption rates and documented hallucination risk — collectively indicate that unstructured AI deployment carries quantifiable and currently unmitigated risk. This reasoning artifact is committed under SDI_DER_v1.1.",
  };
}